// ═══════════════════════════════════════════════════════════════════════════
// AGENT-BRIDGE — canonical state.read PROJECTOR seam (ABB-S6, SM-8)
// ═══════════════════════════════════════════════════════════════════════════
//
// The ONE canonical projector for the `state.read` rpc: `project(route, query,
// cursor, snapshotRef) → StateReadPage`. ABB-S6 OWNS the envelope + the signature +
// the per-route PROJECTOR REGISTRY; it ships REAL bodies for `summary` + `selection`
// ONLY. The other 7 routes (entities / layers / blocks / viewports /
// viewport-frozen-layers / viewport-layer-overrides / spaces) are NOT YET registered
// → they resolve to a documented `validationCode='route-not-yet-projected'` page
// (NEVER a fake empty `{items:[]}` page, ПРИОРИТЕТ #1 — no silent partial support).
//
// COMPLETION OWNER: AGENT-BRIDGE-SCENE-QUERY (Cat B, strict successor of BROWSER) adds
// the 7 route bodies BEHIND this seam — it registers a projector fn per route via
// `registerRouteProjector(route, fn)` (or extends `BUILTIN_ROUTE_PROJECTORS`). It adds
// BODIES, NOT a new seam (design §2 / SM-8). Each follow-up PR is a trivial 2-line
// registration + the body fn — the envelope, the routeRevisions guard, the cursor
// shape, and the validationCode fallthrough all already live here.
//
// LEAF module (ПРИОРИТЕТ #2): imports NOTHING from editorStore / agentBridge / the
// command engine. EVERY scene coupling arrives through the structural `SceneSnapshotRef`
// the CALLER (the `state.read` handler, wired by S7 from the live committed snapshot)
// passes in — a ONE synchronous committed-snapshot ref captured up front ([H2], no
// await mid-projection). So `npm run deps:graph` stays cycles=0 even though sibling
// `ai/*` modules import editorStore (this one does not — it is a pure projection fn).
// ═══════════════════════════════════════════════════════════════════════════

// TYPE-ONLY import (ПРИОРИТЕТ #2): the per-entity colour facet is the canonical typed
// `EntityColor` union (DXF group 62 / 420 / 430) from drawing-model — NOT a re-invented
// local shape (ПРИОРИТЕТ #1, no fake fields). A `import type` erases at compile time so the
// leaf stays store-free + `npm run deps:graph` cycles=0 (the precedent set by
// `ai/aiDispatchContext.ts`, which `import type`s `DrawingDocument` from the SAME package).
import type { EntityColor } from "@dxf-ai-editor/drawing-model";

// The `SnapshotEntityRef.geometry` field below references the geometry-facet union, which now lives in
// the split sibling leaf (`./sceneGeometryFacet`, extracted for the file-size budget). `export *`
// re-exports it for IMPORTERS of this seam, but this module's OWN code needs the explicit `import type`.
import type { SnapshotEntityGeometry } from "./sceneGeometryFacet";

// TYPE-ONLY import of `RenderableEntity` for the INTERNAL-ONLY `blockFlattenedChildren` facet below
// (T8 osnap-query bridge, Finding 4). This is NOT a new dep-graph precedent needing separate
// justification: `ai/sceneQuery/osnapQuery.ts` (a sibling leaf under this same seam) already imports
// VALUE-level from `scene3d/snap`/`scene3d/spatialIndex`/`scene3d/types` in this same PR — this file
// adds only a type-only edge to `scene3d/types` (erased at compile time), consistent with that
// already-established `ai/` → `scene3d/` edge. `scene3d/types.ts` itself is THREE-free at the type
// level (its only value-adjacent import is `import type { OrthographicCamera, … } from "three"`) and
// carries no `editorStore`/`agentBridge` import back into this seam, so no new cycle is introduced.
import type { RenderableEntity } from "../scene3d/types";

/**
 * The 10 documented `state.read` sub-routes (the companion forwards the route opaquely,
 * INV-1; `stateRoute.ts` never reads it). `audit` is DELIBERATELY ABSENT — it is
 * COMPANION-served (INV-11, `auditPollRoute`) and is NEVER forwarded to the browser; the
 * handler rejects it explicitly if ever seen. `osnap-query` (T8) is the 10th — a read-only
 * nearest-endpoint/midpoint/intersection-to-a-point route, NOT an `aiCallable` action.
 */
export const STATE_READ_ROUTES = [
  "summary",
  "selection",
  "entities",
  "layers",
  "blocks",
  "viewports",
  "viewport-frozen-layers",
  "viewport-layer-overrides",
  "spaces",
  "osnap-query",
] as const;

/** A documented `state.read` sub-route name. */
export type StateReadRoute = (typeof STATE_READ_ROUTES)[number];

/** Whether `route` is one of the 9 documented projection routes (vs unknown / `audit`). */
export function isStateReadRoute(route: string): route is StateReadRoute {
  return (STATE_READ_ROUTES as readonly string[]).includes(route);
}

/**
 * The DOCUMENT-LEVEL inventory routes (AGENT-BRIDGE-SCENE-QUERY PR-B): `layers` / `blocks` /
 * `spaces` enumerate a WHOLE DOCUMENT TABLE (the layer table, the block-definitions table, the
 * spaces list) — they are NOT space-scoped. UNLIKE the entity-bearing routes (`summary` /
 * `selection` / `entities`), a `?scope` does NOT filter them and an UNKNOWN `?scope` does NOT
 * neutralize them to empty: a document-level table has no per-space variant to leak, so the
 * #377 unknown-scope neutralizer (which exists to stop an unknown scope from leaking the
 * per-space ENTITY inventory) is INERT for these routes — they always project the full table and
 * always report `resolvedSpaceId: null`. Mirrors the documented contract (fullstack:963-966 —
 * these are document/per-table inventories the agent reads regardless of the active space).
 */
const DOCUMENT_LEVEL_ROUTES: ReadonlySet<StateReadRoute> = new Set<StateReadRoute>([
  "layers",
  "blocks",
  "spaces",
]);

/** Whether a route is a document-level table inventory (NOT space-scoped — see `DOCUMENT_LEVEL_ROUTES`). */
export function isDocumentLevelRoute(route: StateReadRoute): boolean {
  return DOCUMENT_LEVEL_ROUTES.has(route);
}

/**
 * The documented `validationCode` the seam returns for a route whose projection body
 * is not yet registered (the 7 SCENE-QUERY routes today). `stateRoute.ts` maps a
 * `validationCode` to the §8.A HTTP reject (`mapBrowserCodeToHttp`); the agent sees a
 * deterministic "this route is not implemented in this build" rather than a fake empty
 * page that would be indistinguishable from a genuinely empty scene.
 */
export const ROUTE_NOT_YET_PROJECTED = "route-not-yet-projected";

// ── the committed-snapshot ref the projector reads (structural DI — store-free) ──

/**
 * The entity-metadata facet the `summary` / `selection` / `entities` bodies read from the
 * committed snapshot (INV-1 — the projector reads ONLY what it projects). The CALLER (S7)
 * maps the live `geometryDocument` entities onto this shape ONCE, synchronously, before the
 * first projection call ([H2] committed-snapshot isolation).
 *
 * REQUIRED-vs-OPTIONAL MIRRORS `drawingEntityBaseSchema` (packages/drawing-model/src/model.ts
 * ~L100-210, ПРИОРИТЕТ #1 — every field exists on the REAL schema, none invented):
 *   • a field with a schema `.default(...)` is ALWAYS present after parse → REQUIRED here. This
 *     is the by-construction gate: S7's future capture closure MUST populate every required
 *     field (a missed one is a tsc error), so the `entities` route can never emit a half-built
 *     facet. (`id`/`spaceId` are `.min(1)`; `layer`/`linetype`/`lineweight`/`transparency` carry
 *     defaults; `color` defaults to `{kind:"byLayer"}`. `type` is the per-subtype `z.literal`
 *     discriminant — always present, so REQUIRED as a `string`.)
 *   • a schema `.optional()` field is absent unless the DXF carried it → OPTIONAL here
 *     (`linetypeScale`/`ownerHandle`/`materialHandle`/`plotStyleHandle`/`visibility`/`shadowMode`/
 *     `extensionDictionaryHandle`/`arrayGroupId`/`arraySourceOfGroup`/`arrayMode`). Marking these
 *     REQUIRED would wrongly force S7 to synthesize an absent value.
 * (`extraGroups` — the raw vendor-group passthrough — is DELIBERATELY OMITTED: it is a
 * round-trip-only carrier, not an agent-projectable metadata field, and is not in the
 * `entities` allowlist.)
 */
export interface SnapshotEntityRef {
  readonly id: string; // schema `id: z.string().min(1)` — always present.
  readonly type: string; // per-subtype `z.literal(...)` discriminant — always present.
  /** The owning space id (model / a paper-space layout) — for `?scope` resolution. `spaceId: z.string().min(1)`. */
  readonly spaceId: string;
  /** The entity's layer name (for the `summary` per-layer / per-type aggregates). `layer: z.string().default("0")`. */
  readonly layer: string;
  /** Per-entity colour — typed `EntityColor` union (group 62/420/430). `color: …default({kind:"byLayer"})`. */
  readonly color: EntityColor;
  /** Per-entity linetype name (or `ByLayer`/`ByBlock`). `linetype: z.string().default("ByLayer")`. */
  readonly linetype: string;
  /** Per-entity lineweight (or `ByLayer`/`ByBlock`). `lineweight: z.string().default("ByLayer")`. */
  readonly lineweight: string;
  /** Per-entity transparency (or `ByLayer`/`ByBlock`). `transparency: z.string().default("0")`. */
  readonly transparency: string;
  /** DXF group 48 (CELTSCALE-per-entity). `linetypeScale: z.number().optional()`. */
  readonly linetypeScale?: number;
  /** DXF group 330 owner BLOCK_RECORD / object handle. `ownerHandle: z.string().optional()`. */
  readonly ownerHandle?: string;
  /** DXF group 347 AcDbMaterial reference. `materialHandle: z.string().optional()`. */
  readonly materialHandle?: string;
  /** DXF group 390 plot-style-name object handle. `plotStyleHandle: z.string().optional()`. */
  readonly plotStyleHandle?: string;
  /** DXF group 60 (0 = visible, 1 = invisible). `visibility: z.number().int().optional()`. */
  readonly visibility?: number;
  /** DXF group 284 (0 casts+receives … 3 ignores). `shadowMode: z.number().int().optional()`. */
  readonly shadowMode?: number;
  /** Entity ext-dictionary 360 handle. `extensionDictionaryHandle: z.string().optional()`. */
  readonly extensionDictionaryHandle?: string;
  /** ARRAYRECT/ARRAYPOLAR/ARRAYPATH membership group id. `arrayGroupId: z.string().optional()`. */
  readonly arrayGroupId?: string;
  /** Marks a SOURCE (pre-array) member of `arrayGroupId`. `arraySourceOfGroup: z.string().optional()`. */
  readonly arraySourceOfGroup?: string;
  /** Which create command produced this entity. `arrayMode: z.enum(["rect","polar","path"]).optional()`. */
  readonly arrayMode?: "rect" | "polar" | "path";
  /**
   * For a BLOCK INSERT entity, the NAME of the block it references (`blockInsertEntitySchema.blockName`,
   * model.ts:1182). Present ONLY on INSERT/Block entities (absent on a primitive). Used by the `blocks`
   * route to derive a block's `referenceCount` (count of INSERTs naming it) — an O(entities) scan run
   * ONLY when `?includeReferenceCount=true`. It is NOT in the `entities` route allowlist (the entities
   * route projects per-entity metadata, not block cross-references); it is a snapshot-internal facet the
   * `blocks` body reads. AGENT-BRIDGE-SCENE-QUERY PR-B.
   */
  readonly blockName?: string;
  /**
   * The entity's axis-aligned WCS (model-coordinate) extent — the `entityBounds` facet (SCENE-QUERY
   * finding #4, `docs/superpowers/plans/2026-06-21-agent-bridge-browser-design.md:260`). Its
   * OPTIONALITY *is* the `boundsKnown` marker: ABSENT = the bounds are unknown / not-computed for
   * this entity (an entity whose geometry the bounds helper cannot frame); PRESENT = a REAL computed
   * WCS extent. The optionality makes the empty-bounds case distinguishable from a real origin entity
   * — a `Point` at WCS (0,0) reports `{minX:0,minY:0,maxX:0,maxY:0}` (present, real), NEVER conflated
   * with an absent/unknown bound. The shape mirrors the codebase WCS-extent convention
   * (`packages/cad-core/src/index.ts` `EntityBounds` + `command-engine/src/extent.ts` `ModelExtent`:
   * `{minX,minY,maxX,maxY}`) — NOT the SCREEN-space `{left,top,right,bottom}` of the stage selection
   * helper (`scene/entityBounds.ts`, whose `top`/`bottom` are screen-y). Optional `minZ`/`maxZ` carry
   * the Z extent when the entity has one (a flat 2D entity omits them). browser-owned (INV-1).
   * S7's future capture closure computes it via the bounds helper; an entity with no computable
   * bounds OMITS this field (boundsKnown = false).
   */
  readonly entityBounds?: EntityBoundsExtent;
  /**
   * The entity's PER-TYPE GEOMETRY — the discriminated `SnapshotEntityGeometry` shape carrying the
   * REAL drawing coordinates (a Line's endpoints, a Circle's center+radius, an Arc's angles, a
   * polyline's vertices, an INSERT's insertion+scale+rotation, …) projected VERBATIM from the typed
   * `DrawingEntity` union (T1 — the agent READ→find→edit loop needs to SEE geometry, not just
   * metadata). The `kind` discriminant equals the entity's `type` so a consumer narrows by it.
   *
   * NON-LEAN / OPT-IN: geometry is NOT in the entities route's lean default set — it is projected
   * ONLY when the agent requests `?fields=geometry` (or `?fullField=geometry`). The default metadata
   * page stays byte-small (a 100k-entity scene would balloon if every line/hatch carried its full
   * coordinate list by default); a geometry read is an explicit, per-request widening. Mirrors the
   * `referenceCount` / `entityBounds` opt-in pattern.
   *
   * VERBATIM, NO INFERENCE (ПРИОРИТЕТ #1 / `feedback-roundtrip-preserve-source-never-infer`): each
   * facet copies the typed entity's own fields exactly (coordinates, angles, flags) — never derived,
   * rounded, or simplified. An OPTIONAL geometry sub-field (a Line's `extrusion`, a vertex `bulge`)
   * is attached only when present on the source. Distinct from `entityBounds` (a DERIVED extent,
   * deferred): geometry is the entity's RAW defining shape, always available from the typed model.
   *
   * This is distinct from `entityBounds`: bounds is a derived axis-aligned box (deferred); geometry
   * is the raw defining shape. A `?fullField=geometry` read gives the UNCAPPED full shape for one
   * large entity (a Hatch with many boundary edges, a Spline with many control points) per page.
   */
  readonly geometry?: SnapshotEntityGeometry;
  /**
   * INTERNAL-ONLY (T8 osnap-query bridge, GitHub Codex PR #528 Finding 1) — the SAME per-type
   * `SnapshotEntityGeometry` projection as `geometry`, but UNCAPPED (`toEntityGeometryUncapped`,
   * NOT `toEntityGeometry`'s `capGeometry` byte-cap). The cap exists to protect the `entities`
   * route's `?fullField=geometry` WIRE payload from exceeding the companion's per-frame budget when
   * serialized; `osnap-query` never re-serializes the raw geometry over the wire (its output is a
   * small `{mode,point,ownerEntityId,distanceWorld}`), so the cap would incidentally drop a huge
   * Spline/LWPOLYLINE/Hatch entity from the OSNAP candidate set for a concern that does not apply
   * to this internal computation. NEVER add this to `ROUTE_QUERY_ALLOWLISTS.entities.fields` — it
   * is not part of the `entities` route's public projectable field set, only read internally by
   * `ai/sceneQuery/osnapQuery.ts`'s route body.
   */
  readonly rawGeometry?: SnapshotEntityGeometry;
  /**
   * INTERNAL-ONLY (T8 osnap-query bridge, GitHub Codex PR #528 Finding 4) — for a BLOCK-REFERENCE
   * entity, the WORLD-space flattened child renderables (`blockFromEntity` —
   * `scene3d/adapters/typed/blockFromEntity.ts`, the SAME typed block-flatten the Category-B render
   * path uses: recursion into nested INSERTs, MINSERT grid expansion, cycle guard all included).
   * `SnapshotEntityRef`/`SnapshotEntityGeometry` otherwise carry only the INSERT's OWN placement
   * (blockName/insertion/scale/rotation) — never the block DEFINITION's child entities — so without
   * this facet a line/circle/etc. INSIDE a block reference is invisible to `osnap-query`, even though
   * the interactive canvas OSNAP resolver snaps to flattened block-child geometry.
   *
   * WHICH ENTITY TYPES CARRY IT (@codex PR #785 round 2, F2 — consumer contract vs producer
   * status, stated separately on purpose):
   *   • CONSUMER — `ai/sceneQuery/osnapQuery.ts` resolves this facet for every BLOCK-REFERENCE
   *     entity type: a `"Block"` INSERT and a `"Table"` ACAD_TABLE (which is literally a block
   *     reference to its anonymous `*T<N>` cell-graphics block — `AcDbBlockReference` subclass,
   *     group 2 — the same view `drawing-model::tableAsBlockReference` gives `cad-core`'s
   *     `select.ts` / `bounds.ts`).
   *   • PRODUCER — `state/captureSnapshot.ts`'s `toEntityRef` attaches the getter for
   *     `entity.type === "Block"` today; its `"Table"` arm (`tableAsBlockReference(entity)` →
   *     the SAME `blockFromEntity(…)` + `correctRenderableEntityToWorld` pipeline) is a KNOWN,
   *     DOCUMENTED gap, not a silent one. Until it lands, this facet reads back `undefined` for a
   *     table and the consumer contributes no table snap candidates — never a WRONG one.
   * `undefined` for every entity type the producer does not attach it to. NEVER add this to
   * `ROUTE_QUERY_ALLOWLISTS.entities.fields` — it is not part of the `entities` route's public
   * projectable field set, only read internally by `ai/sceneQuery/osnapQuery.ts`'s route body. Each
   * flattened child carries a SYNTHETIC id (namespaced off the parent INSERT's id, minted by
   * `blockFromEntity`) — not independently addressable/selectable outside block-edit mode; the
   * `osnap-query` contract is "here is the world coordinate + a traceable id", not "here is an
   * entity you can MOVE/select directly".
   *
   * COORDINATE-ORIGIN CORRECTNESS (own-review finding, PR #528 post-round-17): the `.geometry` /
   * `.bounds` on every element here are ALREADY corrected to TRUE document-world coordinates by
   * `state/captureSnapshot.ts`'s `toEntityRef` (`correctRenderableEntityToWorld`,
   * `scene3d/adapters/sceneGeometryWorldCorrection.ts`) — NOT the renderer-only offset-space
   * `blockFromEntity`'s per-kind adapters produce internally under a locked session origin
   * (`scene3d/coordinateOrigin.ts`'s `applyOffset`). A consumer of this facet NEVER has to
   * `unapplyOffset` it itself; that correction already happened at the source, before caching.
   */
  readonly blockFlattenedChildren?: readonly RenderableEntity[];
}

/**
 * The axis-aligned WCS (model-coordinate) extent the `entityBounds` facet carries. Mirrors the
 * established codebase convention (`packages/cad-core/src/index.ts` `EntityBounds`,
 * `command-engine/src/extent.ts` `ModelExtent`): `{minX,minY,maxX,maxY}` in WCS. `minZ`/`maxZ` are
 * OPTIONAL — present only for an entity with a real Z extent (a flat 2D entity omits them). This is
 * a WCS extent (`max ≥ min` on each axis), NOT the screen-space `{left,top,right,bottom}` of the
 * stage selection helper.
 */
export interface EntityBoundsExtent {
  readonly minX: number;
  readonly minY: number;
  readonly maxX: number;
  readonly maxY: number;
  readonly minZ?: number;
  readonly maxZ?: number;
}

// ── per-entity GEOMETRY facet (T1) — split into ./sceneGeometryFacet for the file-size budget ──
// The `SnapshotEntityGeometry` discriminated union + the per-entity byte cap
// (`GEOMETRY_PER_ENTITY_BYTE_CAP`) + the `TruncatedGeometry` oversized-entity marker live in the
// sibling leaf module; barrel-re-exported here so every `from "../ai/sceneSummaryProjector"` import
// (the projector, the route bodies, the tests) keeps its path unchanged (ПРИОРИТЕТ #2 — no churn).
export * from "./sceneGeometryFacet";

// ── the ORDERED-COUNT algorithm — the one-truth sort/cap/truncate contour ──────────────────────

/**
 * One `{ entityType, count }` cell of an ORDERED-COUNT array projecting the RAW entity-group
 * `entityType` field verbatim (model.ts:1875 `rawEntityGroupSchema.entityType`, round-trip-preserve
 * — NOT a decoded/derived label). This is the `rawByType` (document-level, DEFERRED — see below) /
 * `rawChildByType` (`SnapshotBlockRef` below, the `blocks` route) cell shape ONLY.
 *
 * `byType`/`byLayer` (`SnapshotSummaryItem` / `projectSummary` below) do **NOT** use this shape
 * directly — the documented contract (fullstack:1315) names their cells `{type,count}` /
 * `{layer,count}` respectively (mirroring the `SnapshotEntityRef.type`/`.layer` fields each one
 * counts), while `rawByType` is `{entityType,count}` (fullstack:1316, mirroring the RAW group's
 * OWN `entityType` field). Three DISTINCT wire key names for three DISTINCT axes — not one
 * generic shape reused verbatim (GitHub Codex round-1 P2 on this PR: an earlier revision reused
 * `OrderedTypeCount` verbatim for `byType`/`byLayer`, which is the WRONG per-route key). `projectSummary`
 * remaps this shape's `entityType` field to `type`/`layer` at its two call sites — see there.
 *
 * LIVES HERE (the seam), not in a `sceneQuery/*` sibling (ПРИОРИТЕТ #2 — dep-graph direction): the
 * seam OWNS `byType`/`byLayer` (`projectSummary` is defined in THIS file, and calls
 * `buildOrderedTypeCounts` below), and a sibling `ai/sceneQuery/projectionHelpers.ts` ALREADY
 * imports the seam's `clampLimit`/`cursorOffset` (an established one-way edge,
 * `projectionHelpers.ts` → `sceneSummaryProjector.ts`). If this shape/builder lived in
 * `projectionHelpers.ts` instead, `projectSummary` importing it back would form a CYCLE
 * (`sceneSummaryProjector.ts` → `projectionHelpers.ts` → `sceneSummaryProjector.ts`, `npm run
 * deps:graph` cycles≠0 — a hard global gate, not a style preference). `projectionHelpers.ts` /
 * `state/captureSnapshot.ts` (the `blocks` route's S7 capture closure) import `OrderedTypeCount` /
 * `buildOrderedTypeCounts` FROM here.
 *
 * (`rawByType`/`rawEntityCount` — the DOCUMENT-LEVEL aggregate of `DrawingDocument.rawEntityGroups`,
 * fullstack:1316 — are a DEFERRED sibling, NOT implemented by this shape today: no facet on
 * `SceneSnapshotRef` carries a document-wide raw-entity-group tally yet. When that slice ships it
 * reuses this SAME `OrderedTypeCount`/`buildOrderedTypeCounts` pair verbatim — never a fourth
 * parallel ordering implementation.)
 */
export interface OrderedTypeCount {
  readonly entityType: string;
  readonly count: number;
}

/**
 * Build the canonical ORDERED-COUNT array from an `entityType → count` tally: sort DESC by `count`,
 * then ASC by `entityType` as a TOTAL tie-breaker (equal counts NEVER shuffle — fullstack:1315),
 * then cap to `cap` cells. Returns `{ ordered, truncated }` where `truncated` is true iff the tally
 * had MORE distinct keys than the cap (the `*Truncated` flag the contract pairs with every
 * ordered-count array). The ONE-TRUTH sort/cap/tie-break ALGORITHM EVERY ordered-count call site
 * shares — `projectSummary`'s `byType`/`byLayer` below (computed fresh per request, scope-filtered,
 * THEN remapped from this fn's `{entityType,count}` output to their own `{type,count}`/
 * `{layer,count}` documented cell shape) AND the `blocks` route's `rawChildByType` (computed ONCE
 * at capture by `state/captureSnapshot.ts`'s `toBlockRef`, kept AS-IS — `rawChildByType`'s
 * documented cell shape already IS `{entityType,count}`, no remap needed there) — NEITHER may
 * re-implement the sort/cap/tie-break rule itself (ПРИОРИТЕТ #2.1 — one authoritative ordering
 * contour, no parallel diff/protocol; only the OUTPUT KEY NAME varies per call site, never the
 * algorithm).
 *
 * A `Map` tally is prototype-safe BY CONSTRUCTION (a `Map` key literally named `__proto__`/
 * `toString`/`constructor` is a plain own entry — `Map#get`/`Map#set` never consult the prototype
 * chain), so every caller accumulates into a `Map`, never a plain object (#377 P2's `Object.create
 * (null)` mitigation is subsumed: a `Map` gives the SAME guarantee without the null-prototype
 * incantation).
 */
export function buildOrderedTypeCounts(
  tally: ReadonlyMap<string, number>,
  cap: number,
): { ordered: OrderedTypeCount[]; truncated: boolean } {
  const all: OrderedTypeCount[] = [...tally.entries()].map(([entityType, count]) => ({
    entityType,
    count,
  }));
  all.sort((a, b) => {
    if (a.count !== b.count) {
      return b.count - a.count; // DESC by count.
    }
    if (a.entityType === b.entityType) {
      return 0; // total tie (only an identical key — should not happen from a Map).
    }
    return a.entityType < b.entityType ? -1 : 1; // ASC by entityType as the TOTAL tie-breaker.
  });
  const truncated = all.length > cap;
  return { ordered: truncated ? all.slice(0, cap) : all, truncated };
}

/**
 * The `summary` route's `byType` cap (fullstack:1315 — "cap byType 200"). 200 distinct entity TYPES
 * comfortably covers every real DXF/AutoCAD entity kind many times over — a document that trips this
 * cap is pathological, but the cap is the by-construction byte-budget guard the contract mandates
 * regardless of how unlikely.
 */
export const SUMMARY_BY_TYPE_CAP = 200;

/**
 * The `summary` route's `byLayer` cap (fullstack:1315 — "cap byType 200 / byLayer 1000"). Larger than
 * `SUMMARY_BY_TYPE_CAP` because a real drawing legitimately carries far more distinct LAYER names
 * than entity TYPES (a CAD layering standard can define hundreds of discipline/status layers, while
 * the DXF entity-type vocabulary is closed and small).
 */
export const SUMMARY_BY_LAYER_CAP = 1000;

/**
 * The cap the `blocks` route's `rawChildByType` ordered array INHERITS from the summary `byType`
 * contract (fullstack:1315 — "cap byType 200", :1316 — `rawByType`/`rawChildByType` carry "the
 * SAME sort/cap/`*Truncated` contract as `byType`" — the ALGORITHM, not the cell key name, see
 * `OrderedTypeCount`'s doc-comment above). Kept as its OWN literal (not a live `=
 * SUMMARY_BY_TYPE_CAP` alias — `knip`'s duplicate-export check flags two exported bindings that
 * resolve to the identical value) but the "inherits" relationship is a REGRESSION-TESTED invariant
 * (`sceneSummaryOrderedTypeCounts.property.test.ts` asserts `RAW_CHILD_BY_TYPE_CAP ===
 * SUMMARY_BY_TYPE_CAP` directly), so the two caps cannot drift apart unnoticed. 200 distinct child
 * entity-types is far beyond a realistic block (a handful of unsupported child types at most) — the
 * cap is never reached in practice but present for parity.
 */
export const RAW_CHILD_BY_TYPE_CAP = 200;

/**
 * The LAYER-table facet the `layers` route projects (AGENT-BRIDGE-SCENE-QUERY PR-B). A
 * document-level inventory record per `styleTables.layers` entry — NOT space-scoped (the route
 * enumerates the FULL layer table regardless of `?scope`).
 *
 * REQUIRED-vs-OPTIONAL MIRRORS `layerDefinitionSchema` (packages/drawing-model/src/model.ts ~L2065,
 * ПРИОРИТЕТ #1 — every field exists on the REAL schema, none invented). The schema carries a
 * `.default(...)` for EVERY layer field, so every one is ALWAYS present after parse → REQUIRED here
 * (the by-construction gate: S7's future capture closure MUST populate them). The documented
 * contract (`docs/.../2026-06-06-fullstack-gap-closure.md:963` `LayerRecord`) names the SAME RAW set
 * + a DERIVED `entityCount`:
 *   • `name` — `z.string().min(1)` — the layer name (the table key).
 *   • `color` — `z.number().int().default(7)` — the ACI INDEX (NOT the EntityColor union; layers carry
 *     a raw ACI number, group 62). `transparency`/`plotStyle`/`trueColor` are DELIBERATELY ABSENT —
 *     they are NOT on `layerDefinitionSchema` (the contract: "transparency/plotStyle/trueColor
 *     ОТСУТСТВУЮТ→omitted"), so projecting them would be a fake field.
 *   • `on` — the DERIVED contract name for the schema's `visible: z.boolean().default(true)` (On/Off
 *     state, group 62 sign). `on`/`frozen`/`locked` are THREE distinct flags projected verbatim
 *     (the contract: "on/off≠frozen≠locked — ТРИ flag'а verbatim").
 *   • `frozen` / `locked` — `z.boolean()` each (freeze, lock state).
 *   • `linetype` — `z.string().default("Continuous")` — the layer's linetype name.
 *   • `lineweight` — `z.number().default(-3)` — the raw lineweight enum (-3 = Default; a NUMBER, not a
 *     "ByLayer"-style string — distinct from the per-entity `lineweight` string).
 *   • `plottable` — `z.boolean().default(true)` — whether the layer plots.
 *   • `description` — `z.string().default("")`.
 *   • `flags` — `z.number().int().default(0)` — the RAW DXF group-70 flag bitfield (NOT decoded into
 *     booleans — the contract: "flags(raw int) … flags НЕ декодируется в booleans").
 *   • `handle` — `z.string().default("")`.
 *   • `entityCount` — DERIVED (NOT a schema field): the count of entities ON this layer across the
 *     document (the body computes it from `SceneSnapshotRef.entities`, making the `layers` route's
 *     binding GEOMETRY-derived — fullstack:963 "entityCount(из byLayer) … Cursor биндит
 *     geometryRevision+tableRevision"). It is REQUIRED here (always computable, even as 0).
 */
export interface SnapshotLayerRef {
  readonly name: string;
  /** The ACI color INDEX (raw group-62, a number — NOT the EntityColor union). */
  readonly color: number;
  /** On/Off state (the schema's `visible`, projected as the contract name `on`). */
  readonly on: boolean;
  readonly frozen: boolean;
  readonly locked: boolean;
  readonly linetype: string;
  /** The raw lineweight enum (-3 = Default) — a NUMBER (group 370). */
  readonly lineweight: number;
  readonly plottable: boolean;
  readonly description: string;
  /** The RAW group-70 flag bitfield (NOT decoded into booleans). */
  readonly flags: number;
  readonly handle: string;
}

/**
 * The BLOCK-definitions-table facet the `blocks` route projects (AGENT-BRIDGE-SCENE-QUERY PR-B). A
 * document-level inventory record per BLOCK definition — NOT space-scoped.
 *
 * Grounds in `blockDefinitionSchema` (model.ts ~L1907) + `blockRecordSchema` (~L1977) + the documented
 * `BlockRecord` contract (fullstack:964). REQUIRED ⇔ the union schema field carries a `.default(...)`
 * or `.min(1)`; OPTIONAL ⇔ the schema field is `.optional()` OR the contract marks it conditional:
 *   • `name` — `z.string().min(1)` — the block name (the table key).
 *   • `basePoint` — `drawingPoint3DSchema` ({x,y,z}) — the block base/insertion origin. The schema
 *     field is `basePoint`, NOT `origin` (@codex naming finding) — projected verbatim.
 *   • `handle` — `z.string().default("")`.
 *   • `flags` — `z.number().int().default(0)` — the RAW group-70 block flag bitfield.
 *   • `description` — `z.string().default("")`.
 *   • `xrefPathName` — `z.string().default("")` — the xref path, RAW (default `""` for a non-xref).
 *     NO inferred `isXref` boolean (the contract F3-trap: `isXref` = `xrefPathName!==""` is an inference
 *     from an incidental value — the agent classifies from the raw path/status, never a derived bool).
 *   • `xrefStatus` — `xrefStatusSchema` (the full 5-value enum: resolved/unloaded/not-found/
 *     unreferenced/orphaned) — RAW, from `blockDefinitionSchema.xrefStatus`.
 *   • `insertionUnits` — `z.number().int().default(0)` — group-70 INSUNITS (from `blockRecordSchema`).
 *   • `explodable` — `z.boolean().default(true)` (group 280, from `blockRecordSchema`).
 *   • `scalable` — `z.enum(["uniform","non-uniform"])` (group 281, from `blockRecordSchema`).
 *   • `isAnnotative` — `z.boolean().default(false)` (from `blockRecordSchema`).
 *   • `derivedIsAnonymous` — DERIVED via the canonical `blockNaming.ts isAnonymousBlockName` predicate
 *     (the contract: derived-annotation alongside raw `name`, NOT inferred ad-hoc). The body's caller
 *     (S7) computes it; carried here so the body can project it. REQUIRED (always computable).
 *   • `layoutHandle` — `z.string().optional()` (group 340, from `blockRecordSchema`) — OPTIONAL.
 *   • `rawChildCount` — DERIVED count of `BlockDefinition.rawEntityGroups` (unsupported children kept
 *     verbatim — fullstack:964 per-block raw-child signal). REQUIRED (0 when the block has none).
 *   • `rawChildByType` — DERIVED ORDERED ARRAY `{ entityType, count }` of `BlockDefinition.rawEntityGroups`
 *     grouped by `entityType` (model.ts:1875 `rawEntityGroupSchema.entityType`, verbatim — round-trip-
 *     preserve, NO decode). The per-block companion to the scalar `rawChildCount`: it names WHICH unsupported
 *     child types the block holds (esp. for a block whose children are ALL unsupported — the documented
 *     fidelity channel, fullstack:964 / CLAUDE.md ПРИОРИТЕТ #1 "вся БД"). Carries the SAME sort+cap+`*Truncated`
 *     contract as the summary `byType`/`byLayer` (fullstack:938/939/964): sort DESC count → `entityType` ASC
 *     total tie-break, cap `RAW_CHILD_BY_TYPE_CAP` (200, an ALIAS of the summary's `SUMMARY_BY_TYPE_CAP` —
 *     defined ABOVE, alongside `OrderedTypeCount`/`buildOrderedTypeCounts`). REQUIRED (an EMPTY array `[]`
 *     when the block has no unsupported children — never absent, mirroring `rawChildCount: 0`). S7's capture
 *     closure (`state/captureSnapshot.ts`'s `toBlockRef`) builds it ONCE, at capture, via the one-truth
 *     `buildOrderedTypeCounts` helper, from the block's `rawEntityGroups` tally — the `blocks` route body
 *     (`ai/sceneQuery/projectBlocks.ts`) then reads it off the snapshot verbatim (document-level facets are
 *     NOT re-aggregated per request, unlike the space-scoped summary `byType`/`byLayer` below).
 *   • `rawChildByTypeTruncated` — OPTIONAL `true` flag, present ONLY when the block's distinct-child-type count
 *     EXCEEDS the cap (the `*Truncated` companion to the ordered array). Absent (omitted) otherwise.
 *   • `referenceCount` — OPTIONAL, present ONLY when `?includeReferenceCount=true`: the count of
 *     `BlockInsertEntity` references to this block across all spaces (an O(entities) scan, opt-in).
 *     Absent unless requested (the contract: "referenceCount(если материализован)"). The body computes
 *     it from `SceneSnapshotRef.entities`, making the count GEOMETRY-derived → the route ADDITIONALLY
 *     binds `geometryRevision` when the flag is set (fullstack:964).
 */
export interface SnapshotBlockRef {
  readonly name: string;
  /** The block base/insertion origin (the schema field is `basePoint`, NOT `origin`). */
  readonly basePoint: { readonly x: number; readonly y: number; readonly z: number };
  readonly handle: string;
  /** The RAW group-70 block flag bitfield. */
  readonly flags: number;
  readonly description: string;
  /** The xref path, RAW (default `""` for a non-xref). NO inferred `isXref` boolean. */
  readonly xrefPathName: string;
  /** The xref resolution state (full 5-value enum), RAW. */
  readonly xrefStatus: "resolved" | "unloaded" | "not-found" | "unreferenced" | "orphaned";
  /** Default insertion units (group 70 on the BLOCK_RECORD). */
  readonly insertionUnits: number;
  readonly explodable: boolean;
  readonly scalable: "uniform" | "non-uniform";
  readonly isAnnotative: boolean;
  /** Whether the block name is anonymous, via the canonical `isAnonymousBlockName` predicate. */
  readonly derivedIsAnonymous: boolean;
  /** The count of unsupported raw children kept verbatim on the block definition. */
  readonly rawChildCount: number;
  /**
   * The per-block unsupported-child-type breakdown — an ORDERED ARRAY `{ entityType, count }` of the
   * block's `rawEntityGroups` grouped by `entityType` (sort DESC count → `entityType` ASC tie-break,
   * cap `RAW_CHILD_BY_TYPE_CAP`; the SAME contract as the summary `byType`/`byLayer`, fullstack:964).
   * REQUIRED — an EMPTY array when the block has no unsupported children (mirrors `rawChildCount: 0`;
   * never absent). Typed as the seam's own `OrderedTypeCount` (defined ABOVE, alongside
   * `buildOrderedTypeCounts`) — this facet is WHY that shape lives here rather than in a
   * `sceneQuery/*` sibling: keeping it in the seam lets `SnapshotBlockRef` reference it directly,
   * with no second parallel inline shape to drift from it.
   */
  readonly rawChildByType: readonly OrderedTypeCount[];
  /** `true` only when the block's distinct child-type count exceeds the cap (the `*Truncated` flag); else omitted. */
  readonly rawChildByTypeTruncated?: boolean;
  /** The owning LAYOUT handle (group 340) — present only for paper-space blocks. */
  readonly layoutHandle?: string;
}

/**
 * The SPACES facet the `spaces` route projects (AGENT-BRIDGE-SCENE-QUERY PR-B): model space + each
 * paper-space layout. A document-level inventory record per space — NOT `?scope`-filtered.
 *
 * Grounds in `drawingSpaceSchema` (model.ts ~L2105) + `layoutSchema` (~L2030) + the documented
 * `SpaceRecord` contract (fullstack:966 `{spaceId,kind,name,handle,ownerBlockRecordHandle,tabOrder,
 * entityCount,entityBounds,plot/limits/extents}`):
 *   • `spaceId` — `drawingSpaceSchema.id` (`z.string().min(1)`) — the STABLE space id (never the
 *     layout display name; the contract: "spaceId(stable)").
 *   • `kind` — `drawingSpaceSchema.kind` (`z.enum(["model","layout","block-edit","block-test",
 *     "refedit"])`) — RAW. (`block-edit`/`block-test`/`refedit` are transient session kinds; the
 *     route enumerates whatever spaces exist — the contract names `model|layout` as the common case.)
 *   • `name` — `drawingSpaceSchema.name` (`z.string().min(1)`) — display name.
 *   • `isActive` — DERIVED: whether this is the document's ACTIVE space (`spaceId === activeSpaceId`).
 *     A context (active-space) switch flips it — so the route ALSO binds `contextRevision`. REQUIRED.
 *   • `entityCount` — DERIVED: the count of entities in this space (from `SceneSnapshotRef.entities`,
 *     making the count GEOMETRY-derived → the route binds `geometryRevision`, fullstack:966). REQUIRED.
 *   • `entityBounds` — DERIVED OPTIONAL: the union WCS extent of the space's entities, ABSENT when the
 *     space has no entities with computable bounds (the contract: "entityBounds|null" — its ABSENCE
 *     is the empty/unknown marker; a real origin-only space reports a real `{0,0,0,0}`). The body
 *     unions the per-entity `entityBounds` over the space. GEOMETRY-derived.
 *   • `handle` — `layoutSchema.handle` (group 5) — OPTIONAL (a layout space carries it; model space
 *     may not).
 *   • `ownerBlockRecordHandle` — `layoutSchema.ownerBlockRecordHandle` (group 330) — OPTIONAL.
 *   • `tabOrder` — `layoutSchema.tabOrder` (group 71) — OPTIONAL (present for layouts; model is first).
 *   • `pageSetupName` — `layoutSchema.plotSettings.pageSetupName` (group 1) — OPTIONAL page-setup name
 *     (the named page setup). One of the contract's "plot" facets.
 *   • PLOT facet (fullstack:966 — the `plot(layouts)` triple): `paperName` (`layoutSchema.plotSettings.
 *     paperName` / the flat `paperSize`, group 4) + `plotStyleName` (`layoutSchema.plotSettings.
 *     plotStyleName`, group 7 — the CTB/STB plot-style sheet). All OPTIONAL (a model space / a layout
 *     with no plot config omits them) — present only on a laid-out layout.
 *   • LIMITS facet (fullstack:966 — `limits(layouts)`): `minimumLimits` (`layoutSchema.minimumLimits`,
 *     group 10/20 — LIMMIN) + `maximumLimits` (`layoutSchema.maximumLimits`, group 11/21 — LIMMAX), each
 *     a 2D `{x,y}` point. OPTIONAL — present only on a layout (model space omits them).
 *   • EXTENTS facet (fullstack:966 — `extents(layouts)`): `minimumExtents` (`layoutSchema.minimumExtents`,
 *     group 14/24/34 — EXTMIN) + `maximumExtents` (`layoutSchema.maximumExtents`, group 15/25/35 — EXTMAX),
 *     each a 3D `{x,y,z}` point. OPTIONAL — present only on a layout. NB: these are the LAYOUT-object
 *     paper-space limits/extents (a layout's plot framing), DISTINCT from the geometry-derived `entityBounds`
 *     (the union WCS extent of the space's drawn entities) — both are projectable, neither replaces the other.
 *   All PLOT/LIMITS/EXTENTS fields are PRE-COMPUTED snapshot facets S7's capture closure MUST populate from
 *   `layoutSchema.plotSettings` / `layoutSchema` — like every other facet here, PR-B defines + allowlists +
 *   projects them, S7 fills them. Their ABSENCE in PR-B is NOT a bug: a model space (or a layout with no plot
 *   config) legitimately omits them. The byte-cap + `*Truncated` flags on any long plot STRING are applied AT
 *   CAPTURE (fullstack:990), not in the projector. Each string field is also a `?fullField` retrieval target
 *   (allowlisted — see the `spaces` allowlist cell).
 */
export interface SnapshotSpaceRef {
  readonly spaceId: string;
  readonly kind: "model" | "layout" | "block-edit" | "block-test" | "refedit";
  readonly name: string;
  /** Whether this is the document's active space (DERIVED from `activeSpaceId`). */
  readonly isActive: boolean;
  /** The count of entities in this space (DERIVED — geometry-derived). */
  readonly entityCount: number;
  /** The union WCS extent of the space's entities (ABSENT when none computable). */
  readonly entityBounds?: EntityBoundsExtent;
  /** The LAYOUT object handle (group 5) — present for paper-space layouts. */
  readonly handle?: string;
  /** The owning BLOCK_RECORD handle (group 330) — present for paper-space layouts. */
  readonly ownerBlockRecordHandle?: string;
  /** The tab order (group 71) — present for paper-space layouts. */
  readonly tabOrder?: number;
  /** The named page setup (group 1, `plotSettings.pageSetupName`) — present for laid-out layouts. */
  readonly pageSetupName?: string;
  /** The paper-size name (group 4, `plotSettings.paperName` / `paperSize`) — present for laid-out layouts. */
  readonly paperName?: string;
  /** The current plot-style sheet (CTB/STB) name (group 7, `plotSettings.plotStyleName`) — present for layouts. */
  readonly plotStyleName?: string;
  /** LIMMIN — the layout's lower-left limits (group 10/20, `layoutSchema.minimumLimits`) — present for layouts. */
  readonly minimumLimits?: { readonly x: number; readonly y: number };
  /** LIMMAX — the layout's upper-right limits (group 11/21, `layoutSchema.maximumLimits`) — present for layouts. */
  readonly maximumLimits?: { readonly x: number; readonly y: number };
  /** EXTMIN — the layout's lower-left extents (group 14/24/34, `layoutSchema.minimumExtents`) — present for layouts. */
  readonly minimumExtents?: { readonly x: number; readonly y: number; readonly z: number };
  /** EXTMAX — the layout's upper-right extents (group 15/25/35, `layoutSchema.maximumExtents`) — present for layouts. */
  readonly maximumExtents?: { readonly x: number; readonly y: number; readonly z: number };
}

/**
 * The VIEWPORT facet the `viewports` route projects (AGENT-BRIDGE-SCENE-QUERY PR-C). One RICH-raw
 * record per VIEWPORT entity — keyed by the doc-unique entity `id`, NOT by `viewportId` (the
 * group-69 identifier is the only RAW classifier; the `viewportId===1`=paper-space-control
 * convention is documented FOR THE AGENT but is NOT derived into a synthesized `kind` field — the
 * F3 round-trip-preserve trap: never infer a semantic field from an incidental value). A SPACE-SCOPED
 * route (the `viewports` route filters to the resolved space + is neutralized to `[]` under an unknown
 * `?scope`, like `entities`).
 *
 * REQUIRED-vs-OPTIONAL MIRRORS `viewportEntitySchema` (packages/drawing-model/src/model.ts:1382-1579,
 * ПРИОРИТЕТ #1 — every field exists on the REAL schema, none invented; the FULL rich set enumerated, no
 * truncation): a schema `.default(...)` / `.min(1)` / `z.literal` → ALWAYS present after parse → REQUIRED
 * here (the by-construction gate forcing S7's capture to populate it); a schema `.optional()` → OPTIONAL
 * here (absent unless the DXF carried it).
 *   BASE (drawingEntityBaseSchema, model.ts:100-210):
 *     • `id` (`z.string().min(1)`) — the DOC-UNIQUE entity id (the route key). REQUIRED.
 *     • `spaceId` (`z.string().min(1)`) — the owning space (the route's space-scope key + sort head). REQUIRED.
 *     • `layer` / `color` / `linetype` / `lineweight` / `transparency` — base `.default(...)`. REQUIRED.
 *     • `linetypeScale`/`ownerHandle`/`materialHandle`/`plotStyleHandle`/`visibility`/`shadowMode`/
 *       `extensionDictionaryHandle`/`arrayGroupId`/`arraySourceOfGroup`/`arrayMode` — base `.optional()`. OPTIONAL.
 *     • (`extraGroups` — the raw vendor-group passthrough — DELIBERATELY OMITTED: a round-trip-only carrier,
 *       not an agent-projectable field, mirroring `SnapshotEntityRef`.)
 *   VIEWPORT (viewportEntitySchema):
 *     • `type` (`z.literal("Viewport")`) — always "Viewport". REQUIRED.
 *     • `center` (`drawingPointSchema` {x,y}) / `width` / `height` (`z.number().positive()`) — paper-space frame. REQUIRED.
 *     • `viewCenter` ({x,y} `.default`) / `viewHeight` / `viewDirection` ({x,y,z} `.default`) / `viewTarget`
 *       ({x,y,z} `.default`) / `viewTwistAngle` / `lensLength` / `frontClipZ` / `backClipZ` — view-state `.default(...)`. REQUIRED.
 *     • `viewportStatus` (int `.default(1)`) / `displayLocked` (bool `.default(false)`) — `.default(...)`. REQUIRED.
 *     • `viewportCannoscaleName`/`viewportCannoscaleFactor`/`viewportAnnoAllVisible` — per-VP anno scale `.optional()`. OPTIONAL.
 *     • `brightness`/`contrast` — lighting block (group 141/142) `.optional()`. OPTIONAL.
 *     • `viewportId` (int `.optional()`, group 69) — the RAW classifier (NO derived `kind`). OPTIONAL.
 *     • `statusBitFlags` (int `.optional()`, group 90) — RAW status bits. OPTIONAL.
 *     • `snapBase`/`snapSpacing`/`gridSpacing` ({x,y} `.optional()`) + `snapRotation` (number) / `gridMajor` (int) /
 *       `circleZoomPercent` (int) — snap/grid scalars `.optional()`. OPTIONAL.
 *     • `shadePlotMode` (int) / `renderMode` (int) — plot/render mode `.optional()`. OPTIONAL.
 *     • `vpOverrideScale` ({num,denom}) / `vpOverrideUcsName` (string) — EDIT_DWG_VP_OVERRIDES XDATA `.optional()`. OPTIONAL.
 *     • `clippingBoundaryHandle` (string `.optional()`, group 340) — native clip-boundary handle. OPTIONAL.
 *     • (`clipPath`/`clipBoundaryPaperSpace`/`childEntityIds` — DERIVED RENDER facets (resolved clip polygon /
 *       paper-space boundary / membership index), NOT raw VIEWPORT metadata; DELIBERATELY OMITTED — they are the
 *       renderer's projection, not the agent's read-model, and would couple this leaf to render semantics.)
 *   The UNBOUNDED lists are NOT inlined here as full lists on the row — they live on the facet so the
 *   nested routes (and the `viewports` COUNTS) can read them, but the `viewports` route projects only their
 *   COUNTS (`frozenLayersCount`/`frozenLayerHandlesCount`/`layerOverrideHandlesCount`), the full lists living
 *   in the nested drill-downs:
 *     • `frozenLayers` (`z.array(z.string()).default([])`, model.ts:1397) — the per-VP frozen-layer NAMES (the
 *       proprietary EDIT_DWG_VP_OVERRIDES XDATA — the editor's in-memory truth). S7 reads it OFF the entity
 *       VERBATIM (store-free, no reconcile here).
 *     • `frozenLayerHandles` (`z.array(z.string()).optional()`, model.ts:1520, group 331) — the per-VP NATIVE
 *       frozen-layer HANDLES (the file's authoritative native freeze representation), PARALLEL to and
 *       UNRECONCILED with the XDATA `frozenLayers` names. Exposed RAW (ПРИОРИТЕТ #1 — no silent DB truncation);
 *       the handle→name RESOLVE that would unify the two is the deferred `S-VIEWPORT-OVERRIDE-RESOLVE` slice
 *       (model.ts:1517-1519). Absent on the schema ⇒ no native 331 freeze on this VP.
 *     • `layerOverrideHandles` (`z.array({code:int,handle:string}).optional()`, model.ts:1526, groups 335/343/344/91)
 *       — the per-VP layer-property-override RAW `{code,handle}` pairs (round-trip-preserve; the semantic
 *       handle→value RESOLVE is the contract's separately-deferred slice). Absent on the schema ⇒ an empty list here.
 *   DERIVED COUNTS the `viewports` route projects (NOT raw schema fields):
 *     • `frozenLayersCount` — `frozenLayers.length`. REQUIRED (0 when none).
 *     • `frozenLayerHandlesCount` — `frozenLayerHandles.length` (0 when the optional list is absent). REQUIRED —
 *       symmetry with `frozenLayersCount` so an agent sees there ARE native 331 handles even when the XDATA names are empty.
 *     • `layerOverrideHandlesCount` — `layerOverrideHandles.length` (0 when the optional list is absent). REQUIRED.
 */
export interface SnapshotViewportRef {
  // ── base (drawingEntityBaseSchema) ──
  readonly id: string;
  readonly spaceId: string;
  readonly layer: string;
  readonly color: EntityColor;
  readonly linetype: string;
  readonly lineweight: string;
  readonly transparency: string;
  readonly linetypeScale?: number;
  readonly ownerHandle?: string;
  readonly materialHandle?: string;
  readonly plotStyleHandle?: string;
  readonly visibility?: number;
  readonly shadowMode?: number;
  readonly extensionDictionaryHandle?: string;
  readonly arrayGroupId?: string;
  readonly arraySourceOfGroup?: string;
  readonly arrayMode?: "rect" | "polar" | "path";
  // ── viewport-specific (viewportEntitySchema) — REQUIRED (schema `.default`/`.min(1)`/`z.literal`) ──
  readonly type: string; // always "Viewport".
  readonly center: { readonly x: number; readonly y: number };
  readonly width: number;
  readonly height: number;
  readonly viewCenter: { readonly x: number; readonly y: number };
  readonly viewHeight: number;
  readonly viewDirection: { readonly x: number; readonly y: number; readonly z: number };
  readonly viewTarget: { readonly x: number; readonly y: number; readonly z: number };
  readonly viewTwistAngle: number;
  readonly lensLength: number;
  readonly frontClipZ: number;
  readonly backClipZ: number;
  readonly viewportStatus: number;
  readonly displayLocked: boolean;
  // ── viewport-specific OPTIONAL (schema `.optional()`) ──
  readonly viewportCannoscaleName?: string;
  readonly viewportCannoscaleFactor?: number;
  readonly viewportAnnoAllVisible?: boolean;
  readonly brightness?: number;
  readonly contrast?: number;
  /** DXF group 69 — the RAW viewport identifier (NO derived `kind`; viewportId===1 = paper-space control by convention). */
  readonly viewportId?: number;
  readonly statusBitFlags?: number;
  readonly snapBase?: { readonly x: number; readonly y: number };
  readonly snapSpacing?: { readonly x: number; readonly y: number };
  readonly gridSpacing?: { readonly x: number; readonly y: number };
  readonly snapRotation?: number;
  readonly gridMajor?: number;
  readonly circleZoomPercent?: number;
  readonly shadePlotMode?: number;
  readonly renderMode?: number;
  readonly vpOverrideScale?: { readonly num: number; readonly denom: number };
  readonly vpOverrideUcsName?: string;
  readonly clippingBoundaryHandle?: string;
  // ── the UNBOUNDED per-VP lists (read by the nested routes + the `viewports` COUNTS; not inlined on the `viewports` row) ──
  /**
   * The per-VP frozen-layer NAMES — the proprietary EDIT_DWG_VP_OVERRIDES XDATA `frozenLayers`
   * (model.ts:1397), the editor's IN-MEMORY truth. S7 reads this OFF the entity verbatim — it does
   * NOT reconcile it with `frozenLayerHandles` (the native group-331 HANDLES); the two are PARALLEL
   * UNRECONCILED representations and resolving handles→names is the separately-deferred
   * `S-VIEWPORT-OVERRIDE-RESOLVE` slice (model.ts:1517-1519). A viewport imported with native 331
   * handles but no XDATA names has this EMPTY (its frozen layers surface via `frozenLayerHandles`).
   */
  readonly frozenLayers: readonly string[];
  /**
   * The per-VP NATIVE frozen-layer HANDLES — DXF group 331 soft-pointers to the frozen LAYER records
   * (model.ts:1520, `.optional()`), round-tripped verbatim. The file's authoritative native freeze
   * representation, PARALLEL to (and UNRECONCILED with) the XDATA `frozenLayers` names. Exposed RAW so
   * an agent reading a viewport imported with only 331 handles still SEES its frozen layers (ПРИОРИТЕТ
   * #1 — no silent DB truncation); the handle→name RESOLVE is the deferred `S-VIEWPORT-OVERRIDE-RESOLVE`
   * slice. OPTIONAL (mirrors the schema `.optional()`) — absent ⇒ no native 331 freeze on this VP.
   */
  readonly frozenLayerHandles?: readonly string[];
  /**
   * The per-VP layer-property-override RAW `{code,handle}` pairs (groups 335/343/344/91) — round-trip-
   * preserve, NO decode. OPTIONAL (mirrors the schema `.optional()`, model.ts:1526) — absent ⇒ no native
   * per-VP property overrides on this viewport. The `?` makes the type TELL THE TRUTH so every consumer
   * (the `viewports` count + the `viewport-layer-overrides` drill-down) MUST guard the array access; a
   * non-optional type masked the runtime `undefined` (@codex P2 — an override-free viewport threw).
   */
  readonly layerOverrideHandles?: readonly { readonly code: number; readonly handle: string }[];
}

/**
 * The ONE synchronous committed-snapshot ref the projector reads (no await
 * mid-projection, [H2]). The CALLER captures it from the live store ONCE and passes the
 * SAME ref for every page of a paginated read (the cursor carries the binding
 * `routeRevisions` so the companion detects a mid-paging mutation). STRUCTURAL so the
 * projector never imports editorStore.
 */
export interface SceneSnapshotRef {
  /** All entities across all spaces (the projector filters by `resolvedSpaceId`). */
  readonly entities: readonly SnapshotEntityRef[];
  /**
   * The full LAYER table (document-level — the `layers` route enumerates ALL regardless of `?scope`).
   * AGENT-BRIDGE-SCENE-QUERY PR-B; S7 maps `geometryDocument.styleTables.layers` onto this.
   */
  readonly layers: readonly SnapshotLayerRef[];
  /**
   * The full BLOCK-definitions table (document-level — the `blocks` route enumerates ALL regardless
   * of `?scope`). PR-B; S7 maps `geometryDocument.blocks`/`blockRecords` onto this.
   */
  readonly blocks: readonly SnapshotBlockRef[];
  /**
   * The full SPACES list (model + paper-space layouts; document-level — the `spaces` route enumerates
   * ALL regardless of `?scope`). PR-B; S7 maps `geometryDocument.spaces`/`layouts` onto this.
   */
  readonly spaces: readonly SnapshotSpaceRef[];
  /**
   * All VIEWPORT entities across all spaces (the `viewports` route + the two nested drill-downs read
   * this). A SPACE-SCOPED facet — the `viewports` body filters it by `resolvedSpaceId` and the seam
   * NEUTRALIZES it to `[]` under an unknown `?scope` (like `entities`). AGENT-BRIDGE-SCENE-QUERY PR-C;
   * S7 maps the live `geometryDocument` VIEWPORT entities onto this (reconciling `frozenLayers` AT
   * CAPTURE). Distinct from the `entities` facet: a viewport ALSO appears in `entities` (it is an
   * entity) but the RICH viewport-specific fields + the reconciled `frozenLayers`/`layerOverrideHandles`
   * live ONLY here (the entities route does not project viewport-specific metadata).
   */
  readonly viewports: readonly SnapshotViewportRef[];
  /** The current selection membership (ordered) for the `selection` route. */
  readonly selectionIds: readonly string[];
  /**
   * The active space id (`?scope=active-space` resolves to this). `null` ONLY for the
   * EMPTY-scoped projection an UNKNOWN `?scope` yields (#377 P2 — a non-existent space leaks
   * NO document inventory): the unknown-scope empty snapshot neutralizes `activeSpaceId` to
   * `null` (and `spaceIds` to `[]`) so the summary's space inventory is empty, not the real
   * document's. A genuine live document always supplies a string (S7 maps it from the store).
   */
  readonly activeSpaceId: string | null;
  /** All space ids in the document (`?scope=document` spans all; `?scope=<id>` picks one). */
  readonly spaceIds: readonly string[];
  /**
   * The browser-minted binding-revision tuple ([L7]) the projector stamps into EVERY
   * page's `routeRevisions` (the companion re-signs it into the nextCursor; a mid-paging
   * mutation that bumps these is then detected on the next page). The CALLER supplies the
   * ABB-S1 live counters (`snapshotRevision` / `geometryRevision` / `selectionRevision` /
   * `tableRevision` / `contextRevision`); each route picks the SUBSET it binds to.
   */
  readonly revisions: SnapshotRevisions;
  /** The coarse `snapshotRevision` carried on the page response (NOT a cursor key, [L7]). */
  readonly snapshotRevision: number;
  /** The browser reader column-schema version (browser-owned, [L7]) carried in the cursor. */
  readonly projectionVersion: number;
  /**
   * The monotonic per-document swap counter (ABB-S1 `documentEpoch`) pinned at the instant
   * this snapshot was captured. The `state.read` handler cross-checks it against the
   * in-bridge documentEpoch belief cache (1A): a swap that re-keyed the document AFTER this
   * capture (the cache's epoch advanced past this) → the read is stale (`cursor-stale`).
   * `null` for a document with no epoch (pre-pairing / no swap yet).
   */
  readonly documentEpoch: number | null;
  /**
   * The document IDENTITY this snapshot was captured from (ABB-S1 `documentId`), pinned at the
   * capture instant. The `state.read` handler binds the read to BOTH `documentId` AND
   * `documentEpoch` (1A): a swap is a `documentId` CHANGE *or* a `documentEpoch` increase, so an
   * epoch-only check accepts a STALE snapshot when a same-epoch ADOPTION re-keyed the document
   * to a different id (#377 P2 round-3). `null` before pairing / for a document with no id.
   */
  readonly documentId: string | null;
}

/** The ABB-S1 live revision counters the projector binds each route's `routeRevisions` to. */
export interface SnapshotRevisions {
  readonly snapshotRevision: number;
  readonly geometryRevision: number;
  readonly selectionRevision: number;
  readonly tableRevision: number;
  readonly contextRevision: number;
}

// ── the parsed query + decoded cursor the projector reads (structural) ──────────

/**
 * The decoded `state.read` query (the companion forwarded it OPAQUELY; the handler
 * narrowed the shape — values stay browser-adjudicated, INV-1). All fields optional;
 * the projector applies route-relevant ones + rejects bad values (`unknown-sort` /
 * `unknown-field` / `missing-viewport-id` / `unknown-viewport-id`).
 */
export interface StateReadQuery {
  readonly scope?: string;
  readonly limit?: number;
  readonly fields?: readonly string[];
  readonly sort?: string;
  readonly viewportEntityId?: string;
  readonly includeReferenceCount?: boolean;
  readonly fullField?: string;
  readonly consistency?: string;
  /**
   * T8 `osnap-query` — the world-space query point (`osnapX`/`osnapY`) + search radius
   * (`osnapRadius`, world units). Parsed/finite-checked at the seam
   * (`agentBridge/stateReadHandler.ts` `narrowQuery`); SEMANTIC validity (all three present,
   * finite, `osnapRadius > 0`) is a route-body concern (`osnapQuery.ts`'s own
   * `invalid-osnap-query` reject) — mirrors the established seam/body split (e.g. `blocks`'s
   * `sort=count` gate lives in the body, not here).
   */
  readonly osnapX?: number;
  readonly osnapY?: number;
  readonly osnapRadius?: number;
}

/**
 * The decoded projection cursor (the companion's `cursorPayload` relayed opaquely after
 * its HMAC verify, `stateRoute.ts` → `cursorKeyEnvelope.ts`). `offsetKey` is the
 * browser-minted page anchor; `routeRevisions` is the binding tuple the previous page
 * carried (the handler cross-checks it against the live tuple → `cursor-stale` on a
 * mismatch). `resolvedSpaceId` + `projectionVersion` are the OTHER cursor-bound members
 * the companion SIGNS into the envelope (`cursorKeyEnvelope.ts:67`/`:73`) and relays
 * verbatim — the handler cross-checks BOTH against the live page (an active-space switch
 * mid-pagination → a `resolvedSpaceId` mismatch → `cursor-stale`; a browser reader-schema
 * change → a `projectionVersion` mismatch → `cursor-stale`). `null` on the FIRST page (no
 * cursor). `resolvedSpaceId`/`projectionVersion` are `undefined` only when the companion
 * relayed a payload that omitted them (defensive — the cross-check skips an absent member).
 */
export interface DecodedProjectionCursor {
  readonly offsetKey: unknown;
  readonly routeRevisions: Readonly<Record<string, number>>;
  /** The active spaceId the cursor was minted under (`?scope=active-space`); a switch → stale. */
  readonly resolvedSpaceId?: string | null;
  /** The browser reader column-schema version the cursor was minted under; a change → stale. */
  readonly projectionVersion?: number;
}

/**
 * The canonical §3.2 page envelope the BROWSER returns (`rpc-result.result`). Mirrors
 * `apps/node-companion/src/bridgeContext.ts` `StateReadPage` (the companion narrows it to
 * re-sign the cursor + map a validationCode; INV-1 — it never reads `items`). Declared
 * HERE (not imported from node-companion — a forbidden cross-app import, ПРИОРИТЕТ #2)
 * with this reference comment. `routeRevisions` is ALWAYS populated on a paginated page
 * (the companion guard rejects undefined/null; an empty `{}` would relay an
 * empty-revision cursor → a mid-paging mutation undetected — so the projector always
 * emits a route-specific tuple).
 */
export interface StateReadPage {
  readonly items: unknown;
  readonly nextOffsetKey?: unknown;
  readonly routeRevisions?: Readonly<Record<string, number>>;
  readonly resolvedSpaceId?: string | null;
  readonly snapshotRevision?: number;
  readonly byteTruncated?: boolean;
  readonly projectionVersion?: number;
  readonly validationCode?: string;
  readonly total?: number;
}

/**
 * A per-route projector fn. SCENE-QUERY registers one per route to fill a body BEHIND
 * the seam (it never touches the envelope / cursor / guard). Receives the parsed query,
 * the decoded cursor (null on the first page), the committed snapshot ref ([H2]), and
 * the already-resolved space id (the seam resolves `?scope` once). Returns a
 * `StateReadPage` (a success page OR a `{validationCode}` reject page — e.g. an
 * `unknown-sort` it adjudicates).
 */
export type RouteProjector = (input: {
  readonly query: StateReadQuery;
  readonly cursor: DecodedProjectionCursor | null;
  readonly snapshot: SceneSnapshotRef;
  readonly resolvedSpaceId: string | null;
}) => StateReadPage;

// ── resolve ?scope → ScopeResolution (shared by every route) ─────────────────────

/**
 * The discriminated outcome of resolving `?scope` to a concrete space binding. THREE
 * distinct cases — an UNKNOWN scope literal must NEVER collapse to the same value as
 * `document` (a full-document info leak; #377 P2):
 *   • `kind: "all"`     — `?scope=document` (or absent default `active-space` resolving the
 *     active space is a `space`, see below) → span ALL spaces (`resolvedSpaceId: null`).
 *   • `kind: "space"`   — `active-space` (the default) OR a KNOWN literal space id → THAT
 *     space (`resolvedSpaceId: spaceId`).
 *   • `kind: "unknown"` — a literal that is NOT `document` and NOT a known space id → an
 *     UNKNOWN space (`resolvedSpaceId: null` on the page, but an EMPTY scoped projection,
 *     NOT document-wide — mirrors an empty scene for a non-existent space, the documented
 *     intent, WITHOUT leaking full-document counts).
 */
export type ScopeResolution =
  | { readonly kind: "all" }
  | { readonly kind: "space"; readonly spaceId: string }
  | { readonly kind: "unknown" };

/**
 * Resolve the `?scope` query param to a `ScopeResolution`. `active-space` (the default) →
 * the active space (`space`); `document` → `all` (span every space); a literal that IS a
 * known space id → that `space`; a literal that is NEITHER → `unknown` (an EMPTY scoped page,
 * NOT document-wide — closing the #377 P2 leak where an unknown scope returned the same
 * `null` sentinel as `document` and projected FULL-DOCUMENT counts). Shared so the 7
 * SCENE-QUERY routes resolve scope identically.
 */
export function resolveScope(
  scope: string | undefined,
  snapshot: SceneSnapshotRef,
): ScopeResolution {
  if (scope === undefined || scope === "active-space") {
    // `active-space` resolves to the active space; a null active space (a degenerate
    // document with no active space — or the unknown-scope empty snapshot) → `unknown`
    // (an EMPTY scoped page), NEVER a document-wide span.
    return snapshot.activeSpaceId === null
      ? { kind: "unknown" }
      : { kind: "space", spaceId: snapshot.activeSpaceId };
  }
  if (scope === "document") {
    return { kind: "all" }; // span all spaces.
  }
  if (snapshot.spaceIds.includes(scope)) {
    return { kind: "space", spaceId: scope };
  }
  return { kind: "unknown" }; // a non-existent space → empty scoped page (never document-wide).
}

// ── per-route VALUE validation (the by-construction choke-point the 7 successors inherit) ──

/**
 * The validationCodes `validateRouteQuery` adjudicates — the BROWSER-side query-VALUE
 * rejects (INV-1: the companion forwards the values opaquely; the projector owns the
 * verdict). Each maps through the companion's `mapBrowserCodeToHttp` to a §8.A status.
 *   • `unknown-sort`        — `?sort=<x>` where `<x>` ∉ the route's sort allowlist.
 *   • `unknown-field`       — a `?fields=` member ∉ the route's field allowlist.
 *   • `missing-viewport-id` — a viewport-drill-down route called without `?viewportEntityId`.
 *   • `unknown-viewport-id` — `?viewportEntityId=<id>` not a VIEWPORT entity in the snapshot.
 *   • `invalid-osnap-query` — `osnap-query` (T8) called without `?osnapX`/`?osnapY`/
 *     `?osnapRadius`, with a non-finite value, or a non-positive radius. A route-BODY-owned
 *     validation (`osnapQuery.ts`'s own reject, mirroring `projectBlocks.ts`'s
 *     `countSortWithoutFlagReject` pattern) — the shared `validateRouteQuery` seam only checks
 *     sort/fields/viewport-id MEMBERSHIP, not this route's semantic value validity.
 */
export const QUERY_VALIDATION_CODES = {
  unknownSort: "unknown-sort",
  unknownField: "unknown-field",
  missingViewportId: "missing-viewport-id",
  unknownViewportId: "unknown-viewport-id",
  invalidOsnapQuery: "invalid-osnap-query",
} as const;

/**
 * The canonical entity-type literal for a viewport. ONE source of truth for the
 * `?viewportEntityId` existence check (the seam below) so the literal can't drift again.
 * MUST equal `viewportEntitySchema.type` (`z.literal("Viewport")`, drawing-model/model.ts) —
 * the command-engine creates every viewport entity with `type: "Viewport"` (capital V only).
 * A prior `"VIEWPORT"` (all-caps) drift NEVER matched a real viewport → both nested
 * drill-down routes (`viewport-frozen-layers`, `viewport-layer-overrides`) always rejected
 * with `unknown-viewport-id`. Tests MUST mint their viewport fixtures with THIS const so the
 * fixture literal can't diverge from the seam (a future casing regression then fails the suite).
 */
export const VIEWPORT_ENTITY_TYPE = "Viewport";

/**
 * The per-route allowlist of the query VALUES a route accepts: the legal `?sort=` keys, the
 * legal `?fields=` members, and whether the route REQUIRES a `?viewportEntityId`. This is
 * the SINGLE source of truth `validateRouteQuery` reads, keyed by `StateReadRoute` and
 * pinned `satisfies Record<StateReadRoute, RouteQueryAllowlist>` — a NEW route added to
 * `STATE_READ_ROUTES` without an allowlist cell here is a tsc COMPILE ERROR (by-construction
 * completeness, mirrors the companion's `STATE_QUERY_KEY_KIND` gate). ABB-S6 fills the REAL
 * `summary` + `selection` allowlists; the 7 SCENE-QUERY successors are stubbed PERMISSIVE
 * (empty sort/field sets, no viewport requirement) so they are NOT wrongly rejected before
 * their bodies ship — each successor PR replaces its stub with its real allowlist (the same
 * 2-line edit as registering its projector). The `requiresViewportId` flag is set TRUE for
 * the two viewport-drill-down routes per the documented `missing-viewport-id` reject
 * (stateRoute.ts:94 / fullstack:965) so that contract is enforced by construction the moment
 * those routes register a body.
 */
export interface RouteQueryAllowlist {
  /** The legal `?sort=` keys for this route (a `?sort` ∉ this set → `unknown-sort`). */
  readonly sorts: ReadonlySet<string>;
  /** The legal `?fields=` members for this route (a member ∉ this set → `unknown-field`). */
  readonly fields: ReadonlySet<string>;
  /** Whether this route REQUIRES a `?viewportEntityId` (absent/empty → `missing-viewport-id`). */
  readonly requiresViewportId?: boolean;
}

export const ROUTE_QUERY_ALLOWLISTS = {
  // `summary`: a one-page aggregate — no `?sort` (single item), no per-field projection.
  summary: { sorts: new Set<string>(), fields: new Set<string>() },
  // `selection`: ordered membership; the only projected field is `id`, no sort vocabulary.
  selection: { sorts: new Set<string>(), fields: new Set<string>(["id"]) },
  // ── the 7 SCENE-QUERY successors — PERMISSIVE stubs (filled on body registration) ──
  // Stubbed empty/false so they are NOT wrongly value-rejected before their bodies exist
  // (their route still resolves to `route-not-yet-projected` at the registry fallthrough —
  // the validation runs first but, with empty allowlists + no `?sort`/`?fields`, passes for
  // the common no-query case). The two viewport routes carry `requiresViewportId: true` so
  // the `missing-viewport-id` contract is by-construction the moment they register a body.
  // `entities` (AGENT-BRIDGE-SCENE-QUERY PR-A): the per-entity metadata route. The sort
  // allowlist is the CANONICAL EXPLICIT `key:dir` grammar ONLY — a BARE `?sort=type` is NOT
  // accepted (it falls out of this set → `unknown-sort`). The bare→`:asc` alias was REMOVED
  // (@codex PR-A round-2 P2): the companion's `deriveQueryShape` (stateRoute.ts:660-662)
  // cursor-binds the RAW `sortOrder` string, so a continuation that switched spelling
  // (`type` ↔ `type:asc`) would FALSE-STALE as `409 query-shape-changed`. ONE canonical
  // spelling ⇒ no normalization seam ⇒ no drift (the companion stays a FIXED opaque contract,
  // INV-1 — it never learns sort semantics). NO `handle` sort (an entity has no `handle` on
  // the base schema → an all-undefined sort, the handle-asymmetry the body rejects as
  // `unknown-sort` rather than silently no-op). The field allowlist is GENEROUS (the AVAILABLE
  // projectable set — the per-request byte budget is governed by `?fields`, not by a narrow
  // allowlist): every metadata field on `SnapshotEntityRef` that exists on
  // `drawingEntityBaseSchema`.
  entities: {
    sorts: new Set<string>([
      "type:asc",
      "type:desc",
      "layer:asc",
      "layer:desc",
      "id:asc",
      "id:desc",
    ]),
    fields: new Set<string>([
      "id",
      "type",
      "layer",
      "spaceId",
      "color",
      "linetype",
      "lineweight",
      "linetypeScale",
      "transparency",
      "ownerHandle",
      "materialHandle",
      "plotStyleHandle",
      "visibility",
      "shadowMode",
      "extensionDictionaryHandle",
      "arrayGroupId",
      "arraySourceOfGroup",
      "arrayMode",
      "entityBounds",
      // `geometry` (T1): the per-type defining geometry (the `SnapshotEntityGeometry` union) — the
      // REAL coordinates the agent READ→find→edit loop needs. NON-LEAN / opt-in: present only when
      // requested via `?fields=geometry` (NOT in `LEAN_CORE_FIELDS`), so the default metadata page
      // stays byte-small. `?fullField=geometry` gives the UNCAPPED full shape for one large entity
      // (a Hatch with many edges, a Spline with many control points) per single-item page.
      "geometry",
    ]),
  },
  // `layers` (PR-B): the document-level LAYER table. Canonical `key:dir` sorts ONLY (no bare alias,
  // mirroring `entities`). `count` sorts by the DERIVED `entityCount` (always available → no gating,
  // unlike `blocks`). The field allowlist is every projectable `SnapshotLayerRef` member (RAW
  // LayerRecord per fullstack:963; transparency/plotStyle/trueColor DELIBERATELY ABSENT — not on the
  // layer schema). `on` is the contract name for the schema's `visible`.
  layers: {
    sorts: new Set<string>([
      "name:asc",
      "name:desc",
      "color:asc",
      "color:desc",
      "lineweight:asc",
      "lineweight:desc",
      "count:asc",
      "count:desc",
    ]),
    fields: new Set<string>([
      "name",
      "color",
      "on",
      "frozen",
      "locked",
      "linetype",
      "lineweight",
      "plottable",
      "description",
      "flags",
      "handle",
      "entityCount",
    ]),
  },
  // `blocks` (PR-B): the document-level BLOCK-definitions table. `name:asc/desc` always valid;
  // `count:asc/desc` are in the allowlist BUT the BODY rejects them as `unknown-sort` UNLESS
  // `?includeReferenceCount=true` (a count sort needs the count column — fullstack:964 "`?sort=count`
  // требует `?includeReferenceCount=1`, иначе `400 unknown-sort`"). The generic value-validator only
  // checks set MEMBERSHIP; the flag-gating is a body concern (it depends on another query param). The
  // field allowlist is every projectable `SnapshotBlockRef` member; `referenceCount` is allowlisted
  // (so `?fields=referenceCount` parses) but only EMITTED when the flag is set (the body omits it
  // otherwise — an absent optional). NO `isXref` (the inferred-from-incidental F3-trap).
  blocks: {
    sorts: new Set<string>(["name:asc", "name:desc", "count:asc", "count:desc"]),
    fields: new Set<string>([
      "name",
      "basePoint",
      "handle",
      "flags",
      "description",
      "xrefPathName",
      "xrefStatus",
      "insertionUnits",
      "explodable",
      "scalable",
      "isAnnotative",
      "derivedIsAnonymous",
      "rawChildCount",
      "rawChildByType",
      "rawChildByTypeTruncated",
      "layoutHandle",
      "referenceCount",
    ]),
  },
  // `viewports` (PR-C): the RICH-raw per-VIEWPORT entity record (document-wide, NO `?viewportEntityId`).
  // Keyed by the doc-unique entity `id`; the DEFAULT order is the DETERMINISTIC `(spaceId, viewportId, id)`
  // chain (also the stable tie-break). Canonical `key:dir` sorts ONLY (no bare alias, mirroring `entities`):
  // `id`/`spaceId`/`viewportId` (the natural keys). The field allowlist is the FULL rich `SnapshotViewportRef`
  // set + the THREE DERIVED counts (`frozenLayersCount`/`frozenLayerHandlesCount`/`layerOverrideHandlesCount`)
  // — NOT the unbounded `frozenLayers`/`frozenLayerHandles`/`layerOverrideHandles` lists (those live in the
  // nested routes). `frozenLayersCount` (XDATA names) + `frozenLayerHandlesCount` (native 331 handles) are
  // SEPARATE counts of the two PARALLEL UNRECONCILED freeze representations — so an agent sees there ARE
  // native 331 handles even when the XDATA names are empty. NO synthesized `kind` (the RAW `viewportId` is
  // the only classifier — the F3 round-trip-preserve trap).
  viewports: {
    sorts: new Set<string>([
      "id:asc",
      "id:desc",
      "spaceId:asc",
      "spaceId:desc",
      "viewportId:asc",
      "viewportId:desc",
    ]),
    fields: new Set<string>([
      "id",
      "spaceId",
      "layer",
      "color",
      "linetype",
      "lineweight",
      "transparency",
      "linetypeScale",
      "ownerHandle",
      "materialHandle",
      "plotStyleHandle",
      "visibility",
      "shadowMode",
      "extensionDictionaryHandle",
      "arrayGroupId",
      "arraySourceOfGroup",
      "arrayMode",
      "type",
      "center",
      "width",
      "height",
      "viewCenter",
      "viewHeight",
      "viewDirection",
      "viewTarget",
      "viewTwistAngle",
      "lensLength",
      "frontClipZ",
      "backClipZ",
      "viewportStatus",
      "displayLocked",
      "viewportCannoscaleName",
      "viewportCannoscaleFactor",
      "viewportAnnoAllVisible",
      "brightness",
      "contrast",
      "viewportId",
      "statusBitFlags",
      "snapBase",
      "snapSpacing",
      "gridSpacing",
      "snapRotation",
      "gridMajor",
      "circleZoomPercent",
      "shadePlotMode",
      "renderMode",
      "vpOverrideScale",
      "vpOverrideUcsName",
      "clippingBoundaryHandle",
      "frozenLayersCount",
      "frozenLayerHandlesCount",
      "layerOverrideHandlesCount",
    ]),
  },
  // `viewport-frozen-layers` (PR-C): the nested drill-down — the per-VP frozen layers, `?viewportEntityId`
  // REQUIRED (the seam rejects absent → `missing-viewport-id`, non-VIEWPORT id → `unknown-viewport-id`).
  // BOTH PARALLEL UNRECONCILED freeze representations are exposed RAW (ПРИОРИТЕТ #1 — no silent DB
  // truncation): the proprietary XDATA `frozenLayers` NAMES (each item `{ layerName }`) AND the native
  // group-331 `frozenLayerHandles` HANDLES (each item `{ handle }`). NO name↔handle pairing is fabricated
  // (the handle→name RESOLVE is the deferred `S-VIEWPORT-OVERRIDE-RESOLVE` slice). Sort by `layerName:asc/desc`
  // (names) or `handle:asc/desc` (handles); default = list order (names first, then handles).
  "viewport-frozen-layers": {
    sorts: new Set<string>(["layerName:asc", "layerName:desc", "handle:asc", "handle:desc"]),
    fields: new Set<string>(["layerName", "handle"]),
    requiresViewportId: true,
  },
  // `viewport-layer-overrides` (PR-C): the nested drill-down — the per-VP layer-property-override RAW
  // `{code,handle}` pairs (groups 335/343/344/91), `?viewportEntityId` REQUIRED. Projected VERBATIM (NO
  // decode — the semantic handle→value RESOLVE is the contract's separately-deferred slice; this is FULL
  // contract support per ПРИОРИТЕТ #1, the contract itself returns raw handles here). Sort by `code:asc/desc`
  // / `handle:asc/desc`; default = list order (round-trip preserved).
  "viewport-layer-overrides": {
    sorts: new Set<string>(["code:asc", "code:desc", "handle:asc", "handle:desc"]),
    fields: new Set<string>(["code", "handle"]),
    requiresViewportId: true,
  },
  // `spaces` (PR-B): model space + each paper-space layout (document-level). Canonical `key:dir`
  // sorts. `type` sorts by `kind` (the contract names `type/kind`; the facet field is `kind`). The
  // field allowlist is every projectable `SnapshotSpaceRef` member (per fullstack:966 SpaceRecord +
  // the `isActive` flag) — INCLUDING the documented layout plot/limits/extents facets (@codex PR-B
  // round-3 finding #4: `paperName`/`plotStyleName` (plot), `minimumLimits`/`maximumLimits` (limits),
  // `minimumExtents`/`maximumExtents` (extents) — grounded in `layoutSchema`, S7-populated optionals).
  // NB: the documented DEFAULT ordering (model FIRST → layouts by tabOrder ASC → spaceId tie-break) is
  // the body's NATURAL order when no `?sort` is given; an explicit `?sort` overrides it.
  spaces: {
    sorts: new Set<string>(["name:asc", "name:desc", "type:asc", "type:desc"]),
    fields: new Set<string>([
      "spaceId",
      "kind",
      "name",
      "isActive",
      "entityCount",
      "entityBounds",
      "handle",
      "ownerBlockRecordHandle",
      "tabOrder",
      "pageSetupName",
      "paperName",
      "plotStyleName",
      "minimumLimits",
      "maximumLimits",
      "minimumExtents",
      "maximumExtents",
    ]),
  },
  // `osnap-query` (T8): a SINGLE-candidate nearest-point read — no sort vocabulary (at most 1
  // item) and its shape is FIXED (mode/point/ownerEntityId/distanceWorld), not `?fields`-
  // projectable. Mirrors the `summary` cell exactly (a one-page aggregate with no sort/field
  // vocabulary). Semantic value validity (`osnapX`/`osnapY`/`osnapRadius`) is the route BODY's
  // own concern (`QUERY_VALIDATION_CODES.invalidOsnapQuery`), not this generic membership gate.
  "osnap-query": { sorts: new Set<string>(), fields: new Set<string>() },
} satisfies Record<StateReadRoute, RouteQueryAllowlist>;

/**
 * Validate a route's query VALUES against its allowlist BEFORE the route projector runs.
 * Returns a `{validationCode}` reject page on the FIRST bad member (deterministic order:
 * sort → fields → viewport-required → viewport-unknown), else `null` (the query is valid →
 * the seam runs the projector). Called in `projectStateRead` for EVERY route AFTER scope
 * resolution + BEFORE the body, so `summary`/`selection` AND the 7 successors are validated
 * BY CONSTRUCTION (a successor inherits the choke-point — it never re-implements value
 * validation, it only fills its allowlist cell). The shared one-truth for the [L7] value
 * contract (`unknown-sort` / `unknown-field` / `missing-viewport-id` / `unknown-viewport-id`).
 *
 * The viewport-id existence check reads the SNAPSHOT (a `?viewportEntityId` must resolve to
 * a VIEWPORT-type entity in the document) — `unknown-viewport-id` when it does not. An empty
 * (fully-neutralized) snapshot from an unknown `?scope` makes EVERY viewport id unknown,
 * which is the correct behavior (a non-existent scope has no viewports).
 */
export function validateRouteQuery(
  route: StateReadRoute,
  query: StateReadQuery,
  snapshot: SceneSnapshotRef,
): StateReadPage | null {
  // Typed as the interface (the `satisfies` const infers a narrow union where only SOME
  // members carry the optional `requiresViewportId` — read it through `RouteQueryAllowlist`
  // so the optional flag is uniformly present on every route's view).
  const allow: RouteQueryAllowlist = ROUTE_QUERY_ALLOWLISTS[route];
  // (1) `?sort` — the value MUST be a member of the route's sort allowlist VERBATIM → else
  // unknown-sort. The allowlist holds ONLY the canonical explicit `key:dir` forms; a BARE
  // `?sort=type` is NOT in the set → `unknown-sort` (the bare→`:asc` alias was REMOVED, @codex
  // PR-A round-2 P2: the companion's `deriveQueryShape` cursor-binds the RAW `sortOrder`, so a
  // browser-side normalization seam would let a continuation that switched spelling false-stale
  // as `409 query-shape-changed`). ONE canonical spelling — no normalization, no drift.
  if (query.sort !== undefined && query.sort.length > 0) {
    if (!allow.sorts.has(query.sort)) {
      return notYetOrInvalidPage(snapshot, QUERY_VALIDATION_CODES.unknownSort);
    }
  }
  // (2) `?fields` — the FIRST member ∉ the route's field allowlist → unknown-field.
  if (query.fields !== undefined) {
    for (const f of query.fields) {
      if (!allow.fields.has(f)) {
        return notYetOrInvalidPage(snapshot, QUERY_VALIDATION_CODES.unknownField);
      }
    }
  }
  // (2b) `?fullField` — the documented single-field UNCAPPED full-value retrieval (stateRoute.ts
  // :98-102 / fullstack:955/967). A PRESENT `?fullField` MUST name an allowlisted field (the SAME
  // field vocabulary as `?fields`), else `unknown-field` — mirrors the `?fields` check so the
  // by-construction choke-point covers BOTH projection modes. GENERIC across every route (a route
  // with no `fullField` mode just never gets a non-empty value → no-op for it; `summary` has an
  // empty field set so ANY `fullField` is `unknown-field`, correct; `selection`'s sole field `id`
  // is its only projection so `?fullField=id` is already its output — no regression).
  if (query.fullField !== undefined && query.fullField.length > 0) {
    if (!allow.fields.has(query.fullField)) {
      return notYetOrInvalidPage(snapshot, QUERY_VALIDATION_CODES.unknownField);
    }
  }
  // (3) viewport-drill-down routes REQUIRE a non-empty `?viewportEntityId` → missing-viewport-id.
  const viewportId = query.viewportEntityId;
  if (allow.requiresViewportId === true && (viewportId === undefined || viewportId.length === 0)) {
    return notYetOrInvalidPage(snapshot, QUERY_VALIDATION_CODES.missingViewportId);
  }
  // (4) a PRESENT `?viewportEntityId` must resolve to a VIEWPORT entity in the snapshot →
  //     unknown-viewport-id (validated for EVERY route that carries the param, not only the
  //     two required ones — a stray viewport id on a non-viewport route is still a bad value).
  if (viewportId !== undefined && viewportId.length > 0) {
    const resolves = snapshot.entities.some(
      (e) => e.id === viewportId && e.type === VIEWPORT_ENTITY_TYPE,
    );
    if (!resolves) {
      return notYetOrInvalidPage(snapshot, QUERY_VALIDATION_CODES.unknownViewportId);
    }
  }
  return null; // every value is valid → run the route projector.
}

// ── the `summary` body (REAL — ABB-S6) ──────────────────────────────────────────

/**
 * One `{ type, count }` cell of the `summary` route's `byType` ordered array. The documented
 * contract (fullstack:1315) names this cell's key `type` (mirroring `SnapshotEntityRef.type`, the
 * field it counts) — DISTINCT from the shared `OrderedTypeCount` (`{entityType,count}`, reserved
 * for `rawByType`/`rawChildByType`, see that interface's doc-comment above for the full
 * three-shapes-one-algorithm rationale). `projectSummary` below builds this by REMAPPING
 * `buildOrderedTypeCounts`'s `{entityType,count}` output — the sort/cap/truncate ALGORITHM is the
 * ONE-TRUTH shared contour; only this output key differs per route.
 */
export interface SummaryByTypeCount {
  readonly type: string;
  readonly count: number;
}

/**
 * One `{ layer, count }` cell of the `summary` route's `byLayer` ordered array (fullstack:1315 —
 * mirrors `SnapshotEntityRef.layer`, the field it counts). See `SummaryByTypeCount` for why this is
 * a shape DISTINCT from the shared `OrderedTypeCount`.
 */
export interface SummaryByLayerCount {
  readonly layer: string;
  readonly count: number;
}

/**
 * The `summary` route's single-item page shape (`projectSummary` below). REQUIRED fields are
 * ALWAYS computed; the two `*Truncated` companions are OPTIONAL — present ONLY when the
 * respective ordered array was capped (mirrors `SnapshotBlockRef.rawChildByTypeTruncated`'s
 * absent-unless-truncated convention — never `false`, INV-1).
 *   • `entityCount` — the SCOPED entity count (document-wide for `?scope=document`/an unknown
 *     scope's neutralized empty snapshot, else the resolved space's count).
 *   • `byType` — the ORDERED-COUNT array of `{type,count}` cells (fullstack:1315 — "NOT a JSON-map
 *     whose truncation order is undefined"): sort DESC count → `type` ASC total tie-break, built via
 *     the ONE-TRUTH `buildOrderedTypeCounts` (defined above) THEN remapped to the documented
 *     `{type,count}` cell key (see `SummaryByTypeCount`). Capped at `SUMMARY_BY_TYPE_CAP` (200).
 *   • `byLayer` — the ORDERED-COUNT array of `{layer,count}` cells, same algorithm, remapped to
 *     `SummaryByLayerCount`. Capped INDEPENDENTLY at `SUMMARY_BY_LAYER_CAP` (1000) — a real document
 *     legitimately carries far more distinct layers than entity types.
 *   • `byTypeTruncated` / `byLayerTruncated` — OPTIONAL `true` flags, present ONLY when the
 *     respective tally's distinct-key count exceeded its cap.
 *   • `selectionCount` — the SCOPED selection size (#377 P2 — intersect selection ∩ scoped for a
 *     space-scoped read; global for `?scope=document`).
 *   • `spaceIds` / `activeSpaceId` — the document's space inventory (neutralized to `[]`/`null`
 *     under an unknown `?scope`, #377 P2 round-3).
 */
export interface SnapshotSummaryItem {
  readonly entityCount: number;
  readonly byType: readonly SummaryByTypeCount[];
  readonly byTypeTruncated?: boolean;
  readonly byLayer: readonly SummaryByLayerCount[];
  readonly byLayerTruncated?: boolean;
  readonly selectionCount: number;
  readonly spaceIds: readonly string[];
  readonly activeSpaceId: string | null;
}

/**
 * The `summary` route body: a single-item aggregate page describing the scene scope
 * (entity counts by type + by layer, selection size, space inventory). A summary is a
 * ONE-PAGE projection (no pagination — `nextOffsetKey: null`). It binds `routeRevisions`
 * to the ROUTE-SPECIFIC counters ONLY — `geometryRevision` (entity-state add/erase/move/
 * grip/property edit) + `selectionRevision` (the selection size it reports) — NOT the
 * coarse `snapshotRevision`. `snapshotRevision` DUAL-BUMPS on EVERY commit (revisionModel
 * INV-9), so binding it would FALSELY stale a summary continuation after ANY unrelated
 * mutation (a layer rename, a context switch — none of which change the summary's data).
 * It is a RESPONSE ANCHOR (`page.snapshotRevision`), never a cursor-binding key ([L7] —
 * mirrors the companion's `cursorKeyEnvelope.ts:20` "snapshotRevision is DELIBERATELY
 * ABSENT … would stale every cursor"). A mid-read mutation that changes the summary's
 * actual data bumps `geometryRevision`/`selectionRevision`, which IS cursor-detected.
 */
const projectSummary: RouteProjector = ({ snapshot, resolvedSpaceId }) => {
  const scoped =
    resolvedSpaceId === null
      ? snapshot.entities
      : snapshot.entities.filter((e) => e.spaceId === resolvedSpaceId);
  // ORDERED-COUNT `byType`/`byLayer` (fullstack:1315 — "ORDERED ARRAYS … NOT a JSON-map whose
  // truncation order is undefined"; cap byType 200 / byLayer 1000 + `*Truncated`). Tally into a
  // `Map` (prototype-safe BY CONSTRUCTION — a document-supplied key literally named `__proto__`/
  // `toString`/`constructor` is a plain own `Map` entry, never a prototype read/write, subsuming
  // the #377 P2 `Object.create(null)` mitigation this used to need), then run the SAME one-truth
  // `buildOrderedTypeCounts` ALGORITHM the blocks `rawChildByType` facet uses (ПРИОРИТЕТ #2.1 — one
  // authoritative ordering/cap/truncation contour, never a second parallel implementation).
  const byTypeTally = new Map<string, number>();
  const byLayerTally = new Map<string, number>();
  for (const e of scoped) {
    byTypeTally.set(e.type, (byTypeTally.get(e.type) ?? 0) + 1);
    byLayerTally.set(e.layer, (byLayerTally.get(e.layer) ?? 0) + 1);
  }
  const { ordered: byTypeOrdered, truncated: byTypeTruncated } = buildOrderedTypeCounts(
    byTypeTally,
    SUMMARY_BY_TYPE_CAP,
  );
  const { ordered: byLayerOrdered, truncated: byLayerTruncated } = buildOrderedTypeCounts(
    byLayerTally,
    SUMMARY_BY_LAYER_CAP,
  );
  // REMAP the shared `{entityType,count}` builder output to the documented PER-ROUTE cell key
  // (fullstack:1315 — `byType` cells are `{type,count}`, `byLayer` cells are `{layer,count}`;
  // `{entityType,count}` is reserved for `rawByType`/`rawChildByType`, fullstack:1316 — GitHub
  // Codex round-1 P2 on this PR). The sort/cap/truncate ORDER established by `buildOrderedTypeCounts`
  // is preserved verbatim by `.map` — only the key is renamed, never a second ordering pass.
  const byType: SummaryByTypeCount[] = byTypeOrdered.map(({ entityType, count }) => ({
    type: entityType,
    count,
  }));
  const byLayer: SummaryByLayerCount[] = byLayerOrdered.map(({ entityType, count }) => ({
    layer: entityType,
    count,
  }));
  // SCOPED selectionCount (#377 P2): the entity AGGREGATES above are scoped to `resolvedSpaceId`,
  // so the selection size MUST be scoped too — otherwise a selection in ANOTHER space inflates a
  // scoped summary (e.g. `summary?scope=model` reports a paper-space selection's ids). Count only
  // the selected ids whose ENTITY resolves into the in-scope set (intersect selection ∩ scoped).
  // The DOCUMENT scope (`resolvedSpaceId === null`) stays GLOBAL — every selected id counts,
  // unchanged. Build the scoped-id membership set ONCE (O(scoped)); a selected id with no
  // resolving entity (a stale id) is not in-scope → not counted (mirrors an empty space).
  let selectionCount: number;
  if (resolvedSpaceId === null) {
    selectionCount = snapshot.selectionIds.length; // document scope → global (unchanged).
  } else {
    const scopedIds = new Set<string>();
    for (const e of scoped) {
      scopedIds.add(e.id);
    }
    selectionCount = snapshot.selectionIds.reduce(
      (n, id) => (scopedIds.has(id) ? n + 1 : n),
      0,
    );
  }
  const summaryItem: SnapshotSummaryItem = {
    entityCount: scoped.length,
    byType,
    ...(byTypeTruncated ? { byTypeTruncated: true as const } : {}),
    byLayer,
    ...(byLayerTruncated ? { byLayerTruncated: true as const } : {}),
    selectionCount,
    spaceIds: [...snapshot.spaceIds],
    activeSpaceId: snapshot.activeSpaceId,
  };
  return {
    items: [summaryItem],
    nextOffsetKey: null,
    // ROUTE-SPECIFIC binding tuple ([L7]): the summary's data depends on entity STATE
    // (geometryRevision) + the selection size it reports (selectionRevision). The coarse
    // snapshotRevision is DELIBERATELY EXCLUDED (it bumps on every commit → false-stale) —
    // it rides as `page.snapshotRevision` (the anchor below) only.
    routeRevisions: {
      geometryRevision: snapshot.revisions.geometryRevision,
      selectionRevision: snapshot.revisions.selectionRevision,
    },
    resolvedSpaceId,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: 1,
  };
};

// ── the `selection` body (REAL — ABB-S6) ────────────────────────────────────────

/** The default page size when the query omits `?limit` (mirrors the companion clamp). */
export const DEFAULT_PROJECTION_LIMIT = 200;
/** The max page size (the companion clamps `?limit` to this; the browser mirrors it). */
export const MAX_PROJECTION_LIMIT = 1000;

/**
 * Clamp a (possibly absent / out-of-range) `?limit` to `[1, MAX_PROJECTION_LIMIT]`. EXPORTED
 * so the SCENE-QUERY route bodies (`ai/sceneQuery/*`) reuse the SAME clamp rule (the companion
 * mirror) instead of cloning it (dup:check one-truth — a divergent clamp would drift the page
 * size between routes).
 */
export function clampLimit(limit: number | undefined): number {
  if (typeof limit !== "number" || !Number.isFinite(limit)) {
    return DEFAULT_PROJECTION_LIMIT;
  }
  return Math.max(1, Math.min(MAX_PROJECTION_LIMIT, Math.floor(limit)));
}

/**
 * Read the numeric offset from a decoded cursor's `offsetKey` (the browser MINTED it on
 * the prior page, so it is a non-negative integer). A malformed/absent offsetKey → 0
 * (the first page). On a routeRevisions MISMATCH (the binding tuple the cursor carries ≠
 * the live tuple) the caller already rejected with `cursor-stale` BEFORE this — so a
 * cursor that reaches here is revision-consistent. EXPORTED so the SCENE-QUERY route bodies
 * (`ai/sceneQuery/*`) reuse the SAME offset-decode rule instead of cloning it (dup:check
 * one-truth — a divergent decode would mis-page a continuation).
 */
export function cursorOffset(cursor: DecodedProjectionCursor | null): number {
  if (cursor === null) {
    return 0;
  }
  const key = cursor.offsetKey;
  return typeof key === "number" && Number.isInteger(key) && key >= 0 ? key : 0;
}

/**
 * The `selection` route body: the ordered selection membership, PAGINATED by the
 * offsetKey cursor. Binds `routeRevisions` to `selectionRevision` ONLY — a selection
 * change between pages bumps `selectionRevision`, so the projector's revision cross-check
 * catches a stale cursor (`cursor-stale`). `snapshotRevision` is DELIBERATELY EXCLUDED
 * from the binding tuple (it bumps on every commit → would FALSELY stale a selection
 * continuation after any unrelated mutation, e.g. a geometry edit that left the selection
 * untouched); it rides as the `page.snapshotRevision` ANCHOR only ([L7], mirrors
 * `cursorKeyEnvelope.ts:20`). Always emits a POPULATED `routeRevisions` tuple.
 */
const projectSelection: RouteProjector = ({ query, cursor, snapshot }) => {
  const ids = snapshot.selectionIds;
  const limit = clampLimit(query.limit);
  const offset = cursorOffset(cursor);
  const slice = ids.slice(offset, offset + limit);
  const nextOffset = offset + slice.length;
  const hasMore = nextOffset < ids.length;
  return {
    items: slice.map((id) => ({ id })),
    nextOffsetKey: hasMore ? nextOffset : null,
    // ROUTE-SPECIFIC binding tuple ([L7]): the selection page depends ONLY on the
    // selection set membership (selectionRevision). snapshotRevision is EXCLUDED (it bumps
    // on every commit → false-stale) — it rides as `page.snapshotRevision` (the anchor).
    routeRevisions: {
      selectionRevision: snapshot.revisions.selectionRevision,
    },
    resolvedSpaceId: null, // selection spans the whole document (not space-scoped).
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    total: ids.length,
  };
};

// ── the per-route registry (SCENE-QUERY fills the 7 unimplemented routes here) ──

/**
 * The BUILT-IN registry: ABB-S6 ships `summary` + `selection` ONLY. SCENE-QUERY adds the
 * 7 remaining routes here (or via `registerRouteProjector`). A route NOT in this map →
 * `validationCode='route-not-yet-projected'` (the seam fallthrough, NOT a fake page).
 */
const BUILTIN_ROUTE_PROJECTORS: Partial<Record<StateReadRoute, RouteProjector>> = {
  summary: projectSummary,
  selection: projectSelection,
};

/** The mutable registry (starts from the built-ins; SCENE-QUERY may register more). */
const routeProjectors = new Map<StateReadRoute, RouteProjector>(
  Object.entries(BUILTIN_ROUTE_PROJECTORS) as [StateReadRoute, RouteProjector][],
);

/**
 * Register a route projector (SCENE-QUERY's per-route follow-up entry point). Each of
 * the 7 remaining routes is a trivial `registerRouteProjector('entities', projectEntities)`
 * + the body fn — the envelope / cursor / guard / validationCode fallthrough are owned
 * here. Idempotent-overwrite (a re-register replaces). Returns whether it REPLACED a prior.
 */
export function registerRouteProjector(route: StateReadRoute, fn: RouteProjector): boolean {
  const existed = routeProjectors.has(route);
  routeProjectors.set(route, fn);
  return existed;
}

/**
 * Unregister a route projector — restores the route to the `route-not-yet-projected`
 * fallthrough. The completeness pair to `registerRouteProjector` (a SCENE-QUERY hot-swap /
 * a test that registers a probe projector + restores the registry afterward). Returns
 * whether a projector was actually REMOVED. NOTE: removing a BUILT-IN (`summary`/`selection`)
 * is allowed but would re-expose its route to the fallthrough — callers re-register to restore.
 */
export function unregisterRouteProjector(route: StateReadRoute): boolean {
  return routeProjectors.delete(route);
}

/** Whether a route has a registered projector body (vs the not-yet-projected fallthrough). */
export function hasRouteProjector(route: StateReadRoute): boolean {
  return routeProjectors.has(route);
}

/**
 * The canonical projector: route → registered body → `StateReadPage`. An UNKNOWN route
 * (not one of the 9) → `unknown-field` (the route name itself is invalid). A KNOWN but
 * UNREGISTERED route (the 7 SCENE-QUERY routes today) → a documented
 * `route-not-yet-projected` page (the items are empty BUT the validationCode marks it
 * NOT a genuine empty scene). A REGISTERED route → its body (which resolves `?scope`
 * once + projects the page, always with a populated `routeRevisions`).
 *
 * The `audit` route is COMPANION-served (INV-11) and is rejected at the HANDLER level
 * BEFORE this is called — it never reaches the projector.
 */
export function projectStateRead(
  route: string,
  query: StateReadQuery,
  cursor: DecodedProjectionCursor | null,
  snapshot: SceneSnapshotRef,
): StateReadPage {
  if (!isStateReadRoute(route)) {
    return notYetOrInvalidPage(snapshot, "unknown-field");
  }
  const projector = routeProjectors.get(route);
  if (projector === undefined) {
    // KNOWN route, not yet registered → the documented deferral (NOT a fake page).
    return notYetOrInvalidPage(snapshot, ROUTE_NOT_YET_PROJECTED);
  }
  const scope = resolveScope(query.scope, snapshot);
  // DOCUMENT-LEVEL routes (`layers`/`blocks`/`spaces`, PR-B) are NOT space-scoped: they enumerate a
  // whole DOCUMENT TABLE regardless of `?scope`, so NEITHER the unknown-scope neutralizer NOR the
  // space-filter applies to them — they always see the REAL snapshot + report `resolvedSpaceId:
  // null`. A document-level table has no per-space variant to leak, so the #377 neutralizer (which
  // stops an unknown scope leaking the per-space ENTITY inventory) is correctly INERT here.
  const documentLevel = isDocumentLevelRoute(route);
  // The snapshot the PROJECTOR (and the value-validator) sees: for an UNKNOWN scope it is
  // the FULLY-NEUTRALIZED empty snapshot (never document-wide — #377 P2); else the real one.
  // An UNKNOWN scope literal (not `document`, not a known space id) MUST NOT collapse to the
  // `document`-wide projection (#377 P2 — a full-document info leak): every SPACE-SCOPED route
  // (summary/selection/entities/viewports + the two viewport drill-downs) projects against ZERO items
  // (mirrors an empty scene for a non-existent space). ALL document-inventory facets are neutralized
  // (#377 P2 round-3): `entities`/`selectionIds`/`spaceIds: []` + `activeSpaceId: null` so the summary
  // (and any space-inventory projector) cannot leak the real document's list/active space for a
  // non-existent scope. The `viewports` facet (PR-C, a space-scoped facet) is ALSO neutralized to `[]`
  // so the `viewports` route + the two nested drill-downs project nothing for an unknown scope — AND the
  // value-validator's `?viewportEntityId` existence check reads the neutralized `entities` (every
  // viewport id is then unknown → `unknown-viewport-id`), so the nested routes never leak a real
  // viewport's frozen-layers/overrides under a bad scope. The binding `revisions`/`snapshotRevision`/
  // `projectionVersion`/`documentEpoch` are PRESERVED (cursor-binding metadata, not scene inventory).
  // DOCUMENT-LEVEL routes skip this — see above. (NB: the document-level `layers`/`blocks`/`spaces`
  // table arrays are NOT in the spread, so even a space-scoped route's neutralization leaves them
  // intact — but only a document-level route ever reads them, and it bypasses neutralization entirely.)
  const projectionSnapshot: SceneSnapshotRef =
    scope.kind === "unknown" && !documentLevel
      ? { ...snapshot, entities: [], viewports: [], selectionIds: [], spaceIds: [], activeSpaceId: null }
      : snapshot;
  // A document-level route always reports `resolvedSpaceId: null` (it is never space-scoped); a
  // space-scoped route reports the resolved space id (or null for `all`/`unknown`).
  const resolvedSpaceId = documentLevel ? null : scope.kind === "space" ? scope.spaceId : null;
  // PER-ROUTE VALUE VALIDATION (STEP 2 choke-point) — runs AFTER scope resolution + BEFORE
  // the route body for EVERY route (summary/selection AND the 7 successors). The single
  // by-construction gate for the [L7] value contract (`unknown-sort` / `unknown-field` /
  // `missing-viewport-id` / `unknown-viewport-id`); a successor inherits it without
  // re-implementing value validation. Validated against `projectionSnapshot` so a
  // `?viewportEntityId` on an unknown scope is `unknown-viewport-id` (the empty snapshot has
  // no viewports), consistent with the body the projector would run.
  const invalid = validateRouteQuery(route, query, projectionSnapshot);
  if (invalid !== null) {
    return invalid;
  }
  return projector({ query, cursor, snapshot: projectionSnapshot, resolvedSpaceId });
}

/**
 * The reject-page shape for a not-yet-projected / unknown / validation-failed route.
 * Binds NO route revisions: a reject page is NOT a paginated continuation (it has no
 * `nextOffsetKey`), so it has nothing to cursor-bind. `routeRevisions: {}` is the
 * DELIBERATE empty tuple the companion accepts verbatim — its guard
 * (`stateRoute.ts:454`) rejects only `undefined`/`null`, and an empty `{}` is "the
 * browser's DELIBERATE 'this route binds no revisions' statement" (the companion never
 * mints a continuation cursor for a non-paginated reject page anyway, so an empty tuple
 * is correct, not a staleness hole). Binding `snapshotRevision` here would FALSELY stale
 * any (non-existent) reject continuation after any commit. The `validationCode`
 * (`stateRoute.ts` maps it to §8.A) makes the empty `items` unambiguous (NOT a genuine
 * empty scene). The `page.snapshotRevision` ANCHOR is still carried for the response.
 */
function notYetOrInvalidPage(
  snapshot: SceneSnapshotRef,
  validationCode: string,
): StateReadPage {
  return {
    items: [],
    nextOffsetKey: null,
    routeRevisions: {}, // a reject page binds NO route revisions (companion accepts {}).
    resolvedSpaceId: null,
    snapshotRevision: snapshot.snapshotRevision, // RESPONSE ANCHOR (not a cursor-binding key).
    byteTruncated: false,
    projectionVersion: snapshot.projectionVersion,
    validationCode,
    total: 0,
  };
}
